import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from "react";
import { generateObservationReportPDF } from "../../../../../utils/generateObservationReportPDF";
import { formatReportDisplayDate } from "../../../../../utils/safetyObservationReportUtils";
import {
  Clock,
  Eye,
  CheckCircle,
  CalendarDays,
  ClipboardCheck,
  TriangleAlert,
  FolderOpen,
  ArrowLeft,
  ImageIcon,
  X as XIcon,
  Check,
  Upload,
  Download,
  CircleAlert,
  ClipboardList,
  UserRound,
  FileText,
  ShieldCheck,
  Building2,
  UserCheck,
  Activity,
  Signature,
  Plus,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { getCurrentUserId } from "../../../../../utils/UserUtils";
import {
  resolveSafetyUserProfile,
  normalizeRole,
  CHECKER_WORKFLOW_ROLES,
} from "../../../../../utils/safetyProfileHelpers";

import {
  approveSafetyChecklist,
  getSafetyChecklist,
  listSafetyChecklists,
  rejectSafetyChecklist,
  resolveActiveProjectId,
  downloadSafetyReport,
  downloadSafetyObservationReport,
  submitSafetyChecklist,
  listSafetyObservations,
  fetchAllSafetyObservations,
  getSafetyObservationById,
  getSafetyObservationCheckers,
  downloadBlob,
  filenameFromDisposition,
} from "../../../../../api";
import { showToast } from "../../../../../utils/toast";
import SafetyResponsiveSignatureCanvas from "../../../SafetyResponsiveSignatureCanvas";
import SafetyChecklistHistoryModal from "../../SafetyChecklistHistoryModal";
import SafetyChecklistCreateView from "./SafetyChecklistCreateView";
import CreateSafetyObservation from "./CreateSafetyObservation";
import ReviewSafetyObservation from "./ReviewSafetyObservation";
import { autoResizeTextarea, TemplateOptionButtons } from "./Makerdashboard";

import SafetyChecklistReadonlyModal from "../../SafetyChecklistReadonlyModal";
import SafetyObservationReadonlyModal from "../../SafetyObservationReadonlyModal";

import SafetyChecklistSignOffSection from "../../SafetyChecklistSignOffSection";
import SafetyImageAnnotationModal from "../../SafetyImageAnnotationModal";
import {
  isObservationMakerActionable,
  isObservationCheckerActionable,
  isObservationVisibleToChecker,
  getObservationRowAction,
  getObservationStatusBadge,
  getInternalOrganizationName,
} from "./observationVisibilityHelpers";
import { SafetyHeroBanner } from "../../../SafetyBannerArt";

// ─── Shared inline UI helpers ────────────────────────────────
const QuestionBadge = ({ number }) => (
  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-500 text-xs font-bold text-white shadow-sm">
    {number}
  </span>
);

const MetaChip = ({ icon, label, value, tone = "slate" }) => {
  const toneClasses = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
  }[tone];

  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${toneClasses}`}
    >
      {icon}
      {label && <span className="text-muted-foreground/80">{label}</span>}
      <span className="truncate font-semibold">{value}</span>
    </span>
  );
};

const resolveMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base =
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "localhost"
      ? "http://127.0.0.1:8001"
      : "https://konstruct.world/checklists";

  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${base}${clean}`;
};

const resolveProjectIdFromUserAccess = () => {
  try {
    const raw = localStorage.getItem("USER_DATA");
    const user = raw && raw !== "undefined" ? JSON.parse(raw) : null;

    const access = Array.isArray(user?.accesses)
      ? user.accesses.find((a) => a?.active && a?.project_id)
      : null;

    return access?.project_id || "";
  } catch {
    return "";
  }
};

const resolveOrgIdFromSession = () => {
  try {
    const raw = localStorage.getItem("USER_DATA");
    const user = raw && raw !== "undefined" ? JSON.parse(raw) : null;

    return (
      user?.org ||
      user?.org_id ||
      user?.organization_id ||
      user?.orgId ||
      localStorage.getItem("org_id") ||
      localStorage.getItem("organization_id") ||
      ""
    );
  } catch {
    return (
      localStorage.getItem("org_id") ||
      localStorage.getItem("organization_id") ||
      ""
    );
  }
};

const getBlobErrorMessage = async (response, fallbackMessage) => {
  const blob = response?.data;
  const contentType = response?.headers?.["content-type"] || blob?.type || "";

  if (
    !contentType.includes("application/json") ||
    typeof blob?.text !== "function"
  ) {
    return fallbackMessage;
  }

  try {
    const text = await blob.text();
    const parsed = JSON.parse(text);
    return parsed?.detail || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

const PhotoViewButton = ({ url, label = "View Photo" }) => {
  const finalUrl = resolveMediaUrl(url);
  if (!finalUrl) return null;

  return (
    <a
      href={finalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 hover:border-blue-300"
    >
      <Eye className="h-3.5 w-3.5" />
      {label}
    </a>
  );
};

const NoPhotoLabel = () => (
  <span className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground">
    <ImageIcon className="h-3.5 w-3.5" />
    No photo available
  </span>
);

// const PhotoUploadArea = ({
//     id,
//     previewBase64,
//     onFileChange,
//     onRemove,
//     label = "Attach photo",
// }) => {
//     const inputRef = useRef(null);
//     const previewUrl = previewBase64
//         ? typeof previewBase64 === "string"
//             ? resolveMediaUrl(previewBase64)
//             : URL.createObjectURL(previewBase64)
//         : null;

//     const handleRemove = () => {
//         if (inputRef.current) {
//             inputRef.current.value = "";
//         }
//         onRemove?.();
//     };

//     return (
//         <div className="mt-3">
//             <p className="mb-1.5 text-xs font-medium text-muted-foreground">{label}</p>
//             {previewUrl ? (
//                 <div className="relative inline-block">
//                     <img
//                         src={previewUrl}
//                         alt="Preview"
//                         className="h-20 w-20 rounded-lg border border-border object-cover shadow-sm"
//                     />
//                     <button
//                         type="button"
//                         onClick={handleRemove}
//                         className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow transition-colors hover:bg-red-600"
//                         aria-label="Remove uploaded photo"
//                     >
//                         <XIcon className="h-3 w-3" />
//                     </button>
//                 </div>
//             ) : (
//                 <label
//                     htmlFor={`photo-upload-${id}`}
//                     className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/60"
//                 >
//                     <Upload className="h-4 w-4" />
//                     <span>Click to upload or take a photo</span>
//                 </label>
//             )}
//             <input
//                 ref={inputRef}
//                 id={`photo-upload-${id}`}
//                 type="file"
//                 accept="image/*"
//                 onChange={onFileChange}
//                 className="hidden"
//             />
//         </div>
//     );
// };

const PhotoUploadArea = ({
  id,
  previewBase64,
  onFileChange,
  onRemove,
  label = "Attach photo",
}) => {
  const inputRef = useRef(null);
  const [annotationFile, setAnnotationFile] = useState(null);
  const [annotationOpen, setAnnotationOpen] = useState(false);

  const previewUrl = previewBase64
    ? typeof previewBase64 === "string"
      ? resolveMediaUrl(previewBase64)
      : URL.createObjectURL(previewBase64)
    : null;

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      showToast("Please upload a valid image file.", "error");

      if (inputRef.current) {
        inputRef.current.value = "";
      }

      return;
    }

    setAnnotationFile(file);
    setAnnotationOpen(true);
  };

  const handleAnnotatedSave = (annotatedFile) => {
    setAnnotationOpen(false);
    setAnnotationFile(null);

    /*
            Keep existing parent usage working.
            Parent code already expects e.target.files[0].
        */
    onFileChange?.({
      target: {
        files: [annotatedFile],
      },
    });

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleAnnotationClose = () => {
    setAnnotationOpen(false);
    setAnnotationFile(null);

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    setAnnotationOpen(false);
    setAnnotationFile(null);

    onRemove?.();
  };

  return (
    <div className="mt-3">
      <p className="mb-1.5 text-xs font-medium text-muted-foreground">
        {label}
      </p>

      {previewUrl ? (
        <div className="relative inline-block">
          <img
            src={previewUrl}
            alt="Preview"
            className="h-20 w-20 rounded-lg border border-border object-cover shadow-sm"
          />

          <button
            type="button"
            onClick={handleRemove}
            className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow transition-colors hover:bg-red-600"
            aria-label="Remove uploaded photo"
          >
            <XIcon className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <label
          htmlFor={`photo-upload-${id}`}
          className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/60"
        >
          <Upload className="h-4 w-4" />
          <span>Click to upload, mark and attach photo</span>
        </label>
      )}

      <input
        ref={inputRef}
        id={`photo-upload-${id}`}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />

      <SafetyImageAnnotationModal
        open={annotationOpen}
        file={annotationFile}
        onClose={handleAnnotationClose}
        onSave={handleAnnotatedSave}
      />
    </div>
  );
};

const YesNoButtons = ({ value, onChange }) => (
  <div className="flex gap-2">
    {[
      {
        opt: "yes",
        label: "Yes",
        icon: <Check className="h-3.5 w-3.5" />,
        active:
          "border-green-500 bg-green-500 text-white shadow-green-200 shadow-md",
        idle: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100",
      },
      {
        opt: "no",
        label: "No",
        icon: <XIcon className="h-3.5 w-3.5" />,
        active: "border-red-500 bg-red-500 text-white shadow-red-200 shadow-md",
        idle: "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
      },
    ].map(({ opt, label, icon, active, idle }) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border-2 px-4 py-2.5 text-sm font-semibold transition-all ${value === opt ? active : idle}`}
      >
        {icon} {label}
      </button>
    ))}
  </div>
);

const YesNoNaButtons = ({ value, onChange }) => (
  <div className="flex gap-2">
    {[
      {
        opt: "yes",
        label: "Yes",
        icon: <Check className="h-3.5 w-3.5" />,
        active:
          "border-green-500 bg-green-500 text-white shadow-green-200 shadow-md",
        idle: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100",
      },
      {
        opt: "no",
        label: "No",
        icon: <XIcon className="h-3.5 w-3.5" />,
        active: "border-red-500 bg-red-500 text-white shadow-red-200 shadow-md",
        idle: "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
      },
      {
        opt: "na",
        label: "N/A",
        icon: null,
        active:
          "border-yellow-500 bg-yellow-500 text-white shadow-yellow-200 shadow-md",
        idle: "border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100",
      },
    ].map(({ opt, label, icon, active, idle }) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-all ${value === opt ? active : idle}`}
      >
        {icon} {label}
      </button>
    ))}
  </div>
);

const RemarksBubble = ({ label, text }) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
    <span className="font-semibold">{label}:</span> {text}
  </div>
);

const getOptionToneByIndex = (index) => {
  if (index === 0) {
    return {
      active: "border-green-500 bg-green-500 text-white shadow-sm",
      idle: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100",
    };
  }

  if (index === 1) {
    return {
      active: "border-red-500 bg-red-500 text-white shadow-sm",
      idle: "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
    };
  }

  if (index === 2) {
    return {
      active: "border-yellow-500 bg-yellow-500 text-white shadow-sm",
      idle: "border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100",
    };
  }

  return {
    active: "border-slate-500 bg-slate-500 text-white shadow-sm",
    idle: "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100",
  };
};

const ReadOnlyOptionButtons = ({ options = [], value, isCheckbox = false }) => {
  const normalizedOptions =
    Array.isArray(options) && options.length ? options : ["Yes", "No", "N/A"];

  const valueList = isCheckbox
    ? String(value || "")
        .toLowerCase()
        .split(",")
        .map((v) => v.trim())
    : [
        String(value || "")
          .trim()
          .toLowerCase(),
      ];

  return (
    <div className="flex flex-wrap gap-2">
      {normalizedOptions.map((opt, index) => {
        const label =
          typeof opt === "string"
            ? opt
            : opt?.label || opt?.name || opt?.title || "";

        if (!label) return null;

        const normalizedLabel = String(label).trim().toLowerCase();
        const active = valueList.includes(normalizedLabel);
        const toneClasses = getOptionToneByIndex(index);

        return (
          <span
            key={`${label}-${index}`}
            className={`rounded-lg border px-4 py-2 text-sm font-semibold ${
              active
                ? toneClasses.active
                : "border-gray-200 bg-gray-50 text-gray-400"
            }`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
};

const isResubmittedForReview = (sub) =>
  Boolean(
    sub?.is_resubmitted_after_rejection ||
    sub?.latest_checker_reject_remarks ||
    sub?.latest_checker_reject_photo_url,
  );

const getResubmittedMakerName = (sub) =>
  String(
    sub?.latest_maker_name ||
      sub?.maker_name ||
      sub?.maker_user_name ||
      "Maker",
  ).trim();

const getPrimarySubmissionForCheckerView = (item) => {
  const submissions = item?.submissions || [];
  return submissions[0] || null;
};

const isCheckerActionableSubmission = (sub) =>
  String(sub?.status || "").toLowerCase() === "pending_checker";

// Filter Component

const ChecklistFilters = ({
  filters,
  setFilters,
  contractorOptions,
  typeOptions,
  isObservations,
  wingOptions,
  hazardOptions,
  checkerOptions = [],
  checkerLoading = false,
}) => {
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      contractor: "all",
      status: "all",
      type: "all",
      wing: "all",
      hazard: "all",
      risk: "all",
      checker: "all",
    });
  };

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-card p-4 sm:p-5 shadow-2xs">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">Filters</h3>

        <button
          type="button"
          onClick={clearFilters}
          className="rounded-lg border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
        >
          Clear
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            From Date
          </label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => updateFilter("fromDate", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            To Date
          </label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => updateFilter("toDate", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Contractor
          </label>
          <select
            value={filters.contractor}
            onChange={(e) => updateFilter("contractor", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All Contractors</option>
            {contractorOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All</option>
            {isObservations ? (
              <>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="rectification">Rectification</option>
                <option value="re rectification">Re Rectification</option>
              </>
            ) : (
              <>
                <option value="rejected">Rejected</option>
                <option value="approved">Approved</option>
              </>
            )}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Type
          </label>
          <select
            value={filters.type}
            onChange={(e) => updateFilter("type", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All Types</option>
            {isObservations ? (
              <>
                <option value="Unsafe Act">Unsafe Act</option>
                <option value="Unsafe Condition">Unsafe Condition</option>
              </>
            ) : (
              typeOptions.map((title) => (
                <option key={title} value={title}>
                  {title}
                </option>
              ))
            )}
          </select>
        </div>
        {isObservations && (
          <>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Wing
              </label>
              <select
                value={filters.wing}
                onChange={(e) => updateFilter("wing", e.target.value)}
                className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              >
                <option value="all">All Wings</option>
                {wingOptions?.map((wing) => (
                  <option key={wing} value={wing}>
                    {wing}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Hazard
              </label>
              <select
                value={filters.hazard}
                onChange={(e) => updateFilter("hazard", e.target.value)}
                className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              >
                <option value="all">All Hazards</option>
                {hazardOptions?.map((hazard) => (
                  <option key={hazard} value={hazard}>
                    {hazard}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Risk
          </label>
          <select
            value={filters.risk}
            onChange={(e) => updateFilter("risk", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All Risks</option>
            <option value="Low Risk">Low Risk</option>
            <option value="Medium Risk">Medium Risk</option>
            <option value="High Risk">High Risk</option>
          </select>
        </div>

        {isObservations && (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Checker
            </label>
            <select
              value={filters.checker || "all"}
              onChange={(e) => updateFilter("checker", e.target.value)}
              disabled={checkerLoading}
              className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:opacity-50"
            >
              <option value="all">All Checkers</option>
              {checkerOptions?.map((c) => (
                <option key={c.id ?? c.value} value={c.id ?? c.value}>
                  {c.name || c.label || c.display_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

const normalizeFilterValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getChecklistCreatedDate = (item) =>
  item?.created_at ? String(item.created_at).slice(0, 10) : "";

const getChecklistContractor = (item) =>
  String(
    item?.contractor_name ||
      item?.name_of_contractor ||
      item?.safety_report_meta?.name_of_contractor ||
      "",
  ).trim();

const getChecklistType = (item) =>
  String(item?.template_title || item?.name || "").trim();

const isApprovedChecklist = (item) =>
  String(item?.status || "").toLowerCase() === "completed" ||
  String(item?.review_state?.closed_reason || "").toLowerCase() === "approved";

const isRejectedChecklist = (item) =>
  String(item?.status || "").toLowerCase() === "closed_rejected" ||
  Number(item?.maker_rejected_points || 0) > 0;

const applyChecklistFilters = (list, filters) => {
  const isObservations = window.location.pathname.includes(
    "/safety/observations",
  );
  return (list || []).filter((item) => {
    const createdDate = getChecklistCreatedDate(item);

    if (filters.fromDate && (!createdDate || createdDate < filters.fromDate)) {
      return false;
    }

    if (filters.toDate && (!createdDate || createdDate > filters.toDate)) {
      return false;
    }

    if (
      filters.contractor !== "all" &&
      normalizeFilterValue(getChecklistContractor(item)) !==
        normalizeFilterValue(filters.contractor)
    ) {
      return false;
    }

    if (isObservations) {
      if (
        filters.type !== "all" &&
        item.unsafe_act_condition_category !== filters.type
      ) {
        return false;
      }
      if (filters.wing !== "all") {
        const w = filters.wing.toLowerCase();
        const locWing = String(item.location_wing || "").toLowerCase();
        const locComb = String(
          item.location_combined || item.location || "",
        ).toLowerCase();

        const hasWing =
          locWing === w ||
          locWing === `wing ${w}` ||
          locComb.includes(`wing ${w}`) ||
          locComb.startsWith(`${w},`) ||
          locComb === w ||
          locComb.includes(` ${w},`);

        if (!hasWing) {
          return false;
        }
      }
      if (filters.hazard !== "all") {
        let hazardsArray = [];
        try {
          if (item.hazard_categories) {
            hazardsArray =
              typeof item.hazard_categories === "string"
                ? JSON.parse(item.hazard_categories)
                : item.hazard_categories;
            if (!Array.isArray(hazardsArray))
              hazardsArray = [String(item.hazard_categories)];
          }
        } catch (e) {
          hazardsArray = [String(item.hazard_categories)];
        }
        if (!hazardsArray || hazardsArray.length === 0) return false;
        const hasHazard = hazardsArray.some(
          (h) => h === filters.hazard || h.startsWith(filters.hazard + ":"),
        );
        if (!hasHazard) return false;
      }

      if (filters.risk && filters.risk !== "all") {
        if (item.risk !== filters.risk) return false;
      }

      if (filters.checker && filters.checker !== "all") {
        const selectedCheckerId = String(filters.checker).trim();
        const directCheckerId = String(item.checker_id || "").trim();
        const directCheckerName = String(item.checker_name || "").trim().toLowerCase();
        const creatorCheckerId =
          ["CHECKER", "CONTRACTOR_CHECKER"].includes(
            String(item.creator_type || "").toUpperCase(),
          )
            ? String(item.creator_id || "").trim()
            : "";
        const creatorCheckerName =
          ["CHECKER", "CONTRACTOR_CHECKER"].includes(
            String(item.creator_type || "").toUpperCase(),
          )
            ? String(item.creator_name || "").trim().toLowerCase()
            : "";

        const attemptCheckerMatch =
          Array.isArray(item.attempts) &&
          item.attempts.some((att) => {
            if (
              String(att?.checker_id || "").trim() === selectedCheckerId ||
              String(att?.checker_name || "").trim().toLowerCase() === selectedCheckerId.toLowerCase()
            ) {
              return true;
            }
            if (Array.isArray(att?.assignments)) {
              return att.assignments.some(
                (assign) =>
                  (String(assign?.safety_officer_id || "").trim() === selectedCheckerId ||
                   String(assign?.safety_officer_name || "").trim().toLowerCase() === selectedCheckerId.toLowerCase()) &&
                  ["CHECKER", "CONTRACTOR_CHECKER"].includes(
                    String(assign?.assignment_role || "").toUpperCase(),
                  ),
              );
            }
            return false;
          });

        const matchesChecker =
          directCheckerId === selectedCheckerId ||
          creatorCheckerId === selectedCheckerId ||
          directCheckerName === selectedCheckerId.toLowerCase() ||
          creatorCheckerName === selectedCheckerId.toLowerCase() ||
          attemptCheckerMatch;

        if (!matchesChecker) {
          return false;
        }
      }

      if (filters.status !== "all") {
        const status = String(item.status || "").toLowerCase();
        if (filters.status === "open") {
          if (
            status === "approved" ||
            status === "rejected" ||
            status === "closed_rejected" ||
            status === "completed"
          )
            return false;
        } else if (filters.status === "closed") {
          if (
            status !== "approved" &&
            status !== "rejected" &&
            status !== "closed_rejected" &&
            status !== "completed"
          )
            return false;
        } else if (filters.status === "rectification") {
          if (
            !["pending_maker", "pending_contractor_maker"].includes(status)
          ) {
            return false;
          }
        } else if (filters.status === "re rectification") {
          if (
            !["pending_checker", "pending_contractor_checker"].includes(status)
          ) {
            return false;
          }
        }
      }
    } else {
      if (
        filters.type !== "all" &&
        normalizeFilterValue(getChecklistType(item)) !==
          normalizeFilterValue(filters.type)
      ) {
        return false;
      }

      if (filters.status === "approved" && !isApprovedChecklist(item)) {
        return false;
      }

      if (filters.status === "rejected" && !isRejectedChecklist(item)) {
        return false;
      }
    }

    return true;
  });
};

const LEGACY_REPORT_HEADER_FIELDS = [
  { key: "format_no", label: "Format No.", order_index: 1 },
  { key: "revision_no", label: "Revision No.", order_index: 2 },
  { key: "issued_date", label: "Issued Date", order_index: 3 },
  { key: "revision_date", label: "Revision Date", order_index: 4 },
  { key: "project", label: "Project", order_index: 5 },
  {
    key: "inspection_report_no",
    label: "Inspection Report No.",
    order_index: 6,
  },
  { key: "name_of_contractor", label: "Name of Contractor", order_index: 7 },
  { key: "date_of_inspection", label: "Date of Inspection", order_index: 8 },
  { key: "make_model", label: "Make / Model", order_index: 9 },
  { key: "identification_no", label: "Identification No.", order_index: 10 },
  { key: "location", label: "Location", order_index: 11 },
  { key: "name_of_operator", label: "Name of Operator", order_index: 12 },
];

const sortResolvedHeaderFields = (fields = []) =>
  [...fields].sort(
    (a, b) =>
      Number(a.row_index || 0) - Number(b.row_index || 0) ||
      Number(a.column_index || 0) - Number(b.column_index || 0) ||
      Number(a.order_index || 0) - Number(b.order_index || 0),
  );

const getResolvedHeaderFieldsForChecker = (detail) => {
  /*
        New dynamic checklist response:
        Values are already resolved by backend from:
        - fixed template fields
        - maker inputs
        - project/system generated values
    */
  const dynamicFields = Array.isArray(detail?.report_header_fields)
    ? detail.report_header_fields
    : [];

  if (dynamicFields.length > 0) {
    return sortResolvedHeaderFields(
      dynamicFields.filter((field) => field.visible_in_preview !== false),
    );
  }

  /*
        Backward compatibility:
        Old checklists created before dynamic header fields still render
        using report_header_meta / safety_report_meta.
    */
  const legacyMeta =
    detail?.report_header_meta ||
    detail?.safety_report_meta ||
    detail?.report_meta ||
    {};

  return LEGACY_REPORT_HEADER_FIELDS.map((field) => ({
    ...field,
    value: legacyMeta[field.key] || "",
    visible_in_preview: true,
  }));
};

const ReadOnlyHeaderValue = ({ label, value }) => (
  <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </p>

    <p className="mt-1 break-words text-sm font-semibold text-foreground">
      {String(value || "").trim() || "—"}
    </p>
  </div>
);

const ReportHeaderInfoCard = ({ detail }) => {
  const fields = getResolvedHeaderFieldsForChecker(detail);

  if (!fields.length) {
    return null;
  }

  return (
    <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
      <h2 className="mb-4 text-sm font-bold text-foreground">
        Checklist Details
      </h2>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {fields.map((field) => (
          <ReadOnlyHeaderValue
            key={field.key}
            label={field.label}
            value={field.value ?? field.default_value ?? ""}
          />
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// CheckerDashboard
// ─────────────────────────────────────────────
export default function CheckerDashboard() {
  const userId = getCurrentUserId();
  const navigate = useNavigate();

  const currentUserProfile = useMemo(() => {
    return resolveSafetyUserProfile();
  }, []);

  const normalizedCheckerRole = normalizeRole(
    localStorage.getItem("FLOW_ROLE") ||
      localStorage.getItem("ACTIVE_ROLE") ||
      localStorage.getItem("ROLE") ||
      currentUserProfile?.active_role ||
      currentUserProfile?.role ||
      "",
  );

  const isCheckerWorkflowUser =
    CHECKER_WORKFLOW_ROLES.has(normalizedCheckerRole);

  const currentCheckerUserType = String(
    currentUserProfile?.user_type || "",
  )
    .trim()
    .toUpperCase();

  const isContractorCheckerUser =
    isCheckerWorkflowUser && currentCheckerUserType === "EXTERNAL";

  const isInternalCheckerUser =
    isCheckerWorkflowUser && currentCheckerUserType === "INTERNAL";

  const isObservations = window.location.pathname.includes(
    "/safety/observations",
  );

  const [projectId, setProjectId] = useState("");
  const [checklists, setChecklists] = useState([]);
  const [observationSummary, setObservationSummary] = useState(null);
  const [checkerBucketByChecklist, setCheckerBucketByChecklist] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState("dashboard"); // "dashboard" | "inspection" | "verification"
  const [checkerAnswers, setCheckerAnswers] = useState({});
  const [checkerRemarks, setCheckerRemarks] = useState({});
  const [checkerMedia, setCheckerMedia] = useState({});
  const [isSubmitSignatureModalOpen, setIsSubmitSignatureModalOpen] =
    useState(false);
  const [pendingSubmitPayload, setPendingSubmitPayload] = useState(null);
  const [checkerComments, setCheckerComments] = useState({});
  const [reviewerMedia, setReviewerMedia] = useState({});
  const [inspectionRemarks, setInspectionRemarks] = useState({});
  const [verificationRemarks, setVerificationRemarks] = useState({});
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [pendingApprovePayload, setPendingApprovePayload] = useState(null);
  const [signatureSubmitting, setSignatureSubmitting] = useState(false);

  const [pendingRejectPayload, setPendingRejectPayload] = useState(null);
  const [signaturePurpose, setSignaturePurpose] = useState(null);

  const [historyChecklist, setHistoryChecklist] = useState(null);
  const [readonlyChecklist, setReadonlyChecklist] = useState(null);
  const [readonlyPendingStatusLabel, setReadonlyPendingStatusLabel] =
    useState("Pending for Review");

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    contractor: "all",
    status: "all",
    type: "all",
    wing: "all",
    hazard: "all",
    risk: "all",
    checker: "all",
  });
  const [checkerOptions, setCheckerOptions] = useState([]);
  const [checkerLoading, setCheckerLoading] = useState(false);
  const [activeCardFilter, setActiveCardFilter] = useState(null);

  const sigCanvasRef = useRef(null);
  const sigPadRef = useRef(null);
  // Reusable component handles the resize logic now

  // ── data fetching ───────────────────────────────────────────
  const fetchList = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const isObservations = window.location.pathname.includes(
        "/safety/observations",
      );
      const params = {
        template_type: isObservations ? "OBSERVATION" : "SAFETY",
        assigned_to_me: true,
      };
      if (projectId) params.project_id = projectId;
      if (isObservations) {
        const orgId = resolveOrgIdFromSession();
        if (orgId) params.org_id = orgId;
        params.paginate = "false";
      }

      let res;
      if (isObservations) {
        res = await fetchAllSafetyObservations(params);
      } else {
        res = await listSafetyChecklists(params);
      }

      const data = res?.data;
      const rawList = Array.isArray(data) ? data : (data?.results ?? []);
      const list = isObservations
        ? [...rawList].sort((a, b) => {
            const timeA = new Date(a.created_at || 0).getTime();
            const timeB = new Date(b.created_at || 0).getTime();
            if (timeB !== timeA) return timeB - timeA;
            return Number(b.id || 0) - Number(a.id || 0);
          })
        : rawList;
      setChecklists(list);

      if (isObservations && !Array.isArray(data)) {
        setObservationSummary(data?.summary || null);
      } else {
        setObservationSummary(null);
      }

      setCheckerBucketByChecklist({});
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to load checklists.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isObservations) return;
    const effectiveProjectId = projectId || resolveActiveProjectId?.() || "";
    if (!effectiveProjectId) return;

    let isMounted = true;
    const loadCheckers = async () => {
      setCheckerLoading(true);
      try {
        const res = await getSafetyObservationCheckers({ project_id: effectiveProjectId });
        const list = res?.data?.checkers || res?.data?.results || (Array.isArray(res?.data) ? res.data : []);
        if (isMounted) {
          const fromObs = [];
          if (Array.isArray(checklists)) {
            checklists.forEach((obs) => {
              if (obs.checker_id && obs.checker_name) {
                fromObs.push({ id: obs.checker_id, name: obs.checker_name });
              }
              if (
                obs.creator_id &&
                obs.creator_name &&
                ["CHECKER", "CONTRACTOR_CHECKER"].includes(String(obs.creator_type || "").toUpperCase())
              ) {
                fromObs.push({ id: obs.creator_id, name: obs.creator_name });
              }
              if (Array.isArray(obs.attempts)) {
                obs.attempts.forEach((att) => {
                  if (att.checker_id && att.checker_name) {
                    fromObs.push({ id: att.checker_id, name: att.checker_name });
                  }
                  if (Array.isArray(att.assignments)) {
                    att.assignments.forEach((asg) => {
                      if (
                        asg.safety_officer_id &&
                        asg.safety_officer_name &&
                        ["CHECKER", "CONTRACTOR_CHECKER"].includes(String(asg.assignment_role || "").toUpperCase())
                      ) {
                        fromObs.push({ id: asg.safety_officer_id, name: asg.safety_officer_name });
                      }
                    });
                  }
                });
              }
            });
          }

          const map = new Map();
          list.forEach((c) => {
            if (c?.id) map.set(String(c.id), { id: c.id, name: c.name || `Checker #${c.id}` });
          });
          fromObs.forEach((c) => {
            if (c?.id && !map.has(String(c.id))) {
              map.set(String(c.id), { id: c.id, name: c.name || `Checker #${c.id}` });
            }
          });

          const combined = Array.from(map.values()).sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
          );
          setCheckerOptions(combined);
        }
      } catch (err) {
        console.warn("Failed to load checkers dynamically in checker dashboard:", err);
      } finally {
        if (isMounted) setCheckerLoading(false);
      }
    };

    loadCheckers();
    return () => {
      isMounted = false;
    };
  }, [isObservations, projectId, checklists]);

  useEffect(() => {
    const activeProjectId =
      resolveActiveProjectId?.() || resolveProjectIdFromUserAccess();

    setProjectId(String(activeProjectId || ""));
  }, []);

  useEffect(() => {
    if (view === "dashboard") {
      fetchList();
    }
  }, [userId, projectId, view]);

  // ── derived lists ───────────────────────────────────────────
  const statusOf = (c) => String(c.status || "").toLowerCase();

  const currentRoleOf = (c) =>
    String(c.current_assignee_role || "").toUpperCase();

  const rejectedPoints = (c) => Number(c.maker_rejected_points ?? 0);

  const reviewRoundCount = (c) =>
    Number(c?.review_state?.completed_review_rounds ?? 0);

  const isClosedRejected = (c) =>
    statusOf(c) === "closed_rejected" || statusOf(c) === "rejected";

  const isFinalChecklist = (item) => {
    if (isObservations) return isObservationFinal(item);
    return (
      statusOf(item) === "completed" ||
      statusOf(item) === "closed_rejected"
    );
  };




  const isApprovedChecklist = (c) => {
    if (isObservations) return statusOf(c) === "approved";
    return statusOf(c) === "completed";
  };

  const isRejectedChecklist = (c) => {
    if (isObservations) return statusOf(c) === "rejected";
    return statusOf(c) === "closed_rejected";
  };

  const MAKER_PENDING_OBSERVATION_STATUSES = [
    "pending_maker",
    "pending_contractor_maker",
  ];

  const CHECKER_REVIEW_OBSERVATION_STATUSES = [
    "pending_checker",
    "pending_contractor_checker",
  ];

  const FINAL_OBSERVATION_STATUSES = ["approved", "rejected"];

  const isObservationCreatedByCurrentUser = (item) =>
    Number(item?.creator_id) === Number(userId);

  const isObservationPendingMaker = (item) =>
    MAKER_PENDING_OBSERVATION_STATUSES.includes(statusOf(item));

  const isObservationPendingChecker = (item) =>
    CHECKER_REVIEW_OBSERVATION_STATUSES.includes(statusOf(item));

  const isObservationFinal = (item) =>
    FINAL_OBSERVATION_STATUSES.includes(statusOf(item));


const getObservationAttemptNo = (item) =>
  Number(
    item?.current_attempt_no ??
      item?.attempt_no ??
      item?.attempts_count ??
      1,
  );

  const isObservationRecertification = (item) =>
  getObservationAttemptNo(item) > 1;




  const isRectificationCycle = (item) => {
    if (isObservations) return isObservationPendingMaker(item);
    return reviewRoundCount(item) > 0 || rejectedPoints(item) > 0;
  };

  const checkerCanReview = (item) => {
    if (isObservations) {
      if (!isCheckerWorkflowUser) {
        return false;
      }
      return isObservationCheckerActionable(item, userId, currentCheckerUserType);
    }

    return (
      statusOf(item) === "in_progress" &&
      currentRoleOf(item) === "CHECKER"
    );
  };

  // Apply filters to ALL dashboards as requested
  const filteredChecklists = applyChecklistFilters(checklists, filters);

  const checkerAssigned = filteredChecklists.filter((item) => {
    if (isObservations) {
      const status = statusOf(item);
      const isPendingReviewStage = ["pending_checker", "pending_contractor_checker"].includes(status);
      if (!isPendingReviewStage) return false;
      return isObservationVisibleToChecker(item, userId);
    }
    return checkerCanReview(item) && !isRectificationCycle(item);
  });

  const checkerPending = filteredChecklists.filter((item) => {
    if (isObservations) {
      const status = statusOf(item);
      const isPendingRectStage = ["pending_maker", "pending_contractor_maker"].includes(status);
      if (!isPendingRectStage) return false;
      return isObservationVisibleToChecker(item, userId);
    }

    return (
      statusOf(item) === "in_progress" &&
      isRectificationCycle(item) &&
      ["MAKER", "CHECKER"].includes(currentRoleOf(item))
    );
  });

  const observationsRaisedByMe = isObservations
    ? filteredChecklists.filter(isObservationCreatedByCurrentUser)
    : [];

  // Filter options should come from RAW checklists so users can select anything available
  const contractorOptions = Array.from(
    new Set(checklists.map(getChecklistContractor).filter(Boolean)),
  ).sort();

  const typeOptions = Array.from(
    new Set(checklists.map(getChecklistType).filter(Boolean)),
  ).sort();

  const wingOptions = ["A", "B", "C", "D", "E", "F", "G", "NTA"];

  const hazardOptions = [
    "1. Physical Hazard",
    "2. Biological Hazard",
    "3. Chemical Hazard",
    "4. Mechanical Hazard",
    "5. Ergonomical Hazard",
    "6. Environmental Hazard",
    "7. Fire/Explosion Hazard",
    "8. Electrical Hazard",
    "9. Psychological Hazard",
    "10. Other Hazards",
  ];

  const checkerCompleted = filteredChecklists.filter((c) => {
    if (isObservations) {
      const status = statusOf(c);
      const isFinal = ["approved", "rejected"].includes(status);
      if (!isFinal) return false;
      return isObservationVisibleToChecker(c, userId);
    }
    return isFinalChecklist(c);
  });

  const hasActiveDropdownFilters = Boolean(
    (filters.fromDate && filters.fromDate !== "") ||
    (filters.toDate && filters.toDate !== "") ||
    (filters.contractor && filters.contractor !== "all") ||
    (filters.wing && filters.wing !== "all") ||
    (filters.hazard && filters.hazard !== "all") ||
    (filters.type && filters.type !== "all") ||
    (filters.risk && filters.risk !== "all") ||
    (filters.status && filters.status !== "all")
  );

  const checkerTotal = isObservations
    ? Number(
        (hasActiveDropdownFilters ? filteredChecklists.length : null) ??
        observationSummary?.total_raised_observations ??
        checklists.length
      )
    : filteredChecklists.length;

  const openObservationsCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter((item) => !isObservationFinal(item)).length
          : null) ??
        observationSummary?.open_observations ??
        filteredChecklists.filter((item) => !isObservationFinal(item)).length
      )
    : 0;

  const checkerApprovedCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter((item) => ["approved", "completed"].includes(statusOf(item))).length
          : null) ??
        observationSummary?.closed_observations ??
        observationSummary?.approved_observations ??
        filteredChecklists.filter((item) => ["approved", "completed"].includes(statusOf(item))).length
      )
    : checkerCompleted.filter(isApprovedChecklist).length;

  const checkerRejectedCount = isObservations
    ? checkerCompleted.filter((item) => statusOf(item) === "rejected").length
    : checkerCompleted.filter(isRejectedChecklist).length;

  const pendingRectificationCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter(
              (item) =>
                ["pending_maker", "pending_contractor_maker"].includes(statusOf(item)) &&
                Number(item.current_attempt_no || 1) <= 1,
            ).length
          : null) ??
          observationSummary?.pending_rectification ??
          checkerPending.filter(
            (item) => Number(item.current_attempt_no || 1) <= 1,
          ).length,
      )
    : checkerPending.length;

  const pendingReRectificationCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter(
              (item) =>
                ["pending_maker", "pending_contractor_maker"].includes(statusOf(item)) &&
                Number(item.current_attempt_no || 0) > 1,
            ).length
          : null) ??
          observationSummary?.pending_re_rectification ??
          checkerPending.filter(
            (item) => Number(item.current_attempt_no || 0) > 1,
          ).length,
      )
    : checkerAssigned.length;

  const totalPendingRectificationCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter((item) =>
              ["pending_maker", "pending_contractor_maker"].includes(statusOf(item)),
            ).length
          : null) ??
          observationSummary?.total_pending_rectification ??
          (observationSummary?.pending_rectification !== undefined &&
          observationSummary?.pending_re_rectification !== undefined
            ? Number(observationSummary.pending_rectification || 0) +
              Number(observationSummary.pending_re_rectification || 0)
            : checkerPending.length),
      )
    : checkerPending.length;

  const unsafeActCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter(
              (item) =>
                String(item.unsafe_act_condition_category || "").toLowerCase() === "unsafe act",
            ).length
          : null) ??
          observationSummary?.unsafe_act_observations ??
          filteredChecklists.filter(
            (item) =>
              String(item.unsafe_act_condition_category || "").toLowerCase() === "unsafe act",
          ).length,
      )
    : 0;

  const unsafeConditionCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter(
              (item) =>
                String(item.unsafe_act_condition_category || "").toLowerCase() === "unsafe condition",
            ).length
          : null) ??
          observationSummary?.unsafe_condition_observations ??
          filteredChecklists.filter(
            (item) =>
              String(item.unsafe_act_condition_category || "").toLowerCase() === "unsafe condition",
          ).length,
      )
    : 0;

  const lowRiskCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter((item) => String(item.risk || "").toLowerCase().includes("low")).length
          : null) ??
          observationSummary?.low_risk ??
          filteredChecklists.filter((item) => String(item.risk || "").toLowerCase().includes("low")).length,
      )
    : 0;

  const mediumRiskCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter((item) => String(item.risk || "").toLowerCase().includes("medium")).length
          : null) ??
          observationSummary?.medium_risk ??
          filteredChecklists.filter((item) => String(item.risk || "").toLowerCase().includes("medium")).length,
      )
    : 0;

  const highRiskCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter((item) => String(item.risk || "").toLowerCase().includes("high")).length
          : null) ??
          observationSummary?.high_risk ??
          filteredChecklists.filter((item) => String(item.risk || "").toLowerCase().includes("high")).length,
      )
    : 0;

  const now = new Date();

  const overdueObservationsCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter((item) => {
              if (isObservationFinal(item)) return false;
              if (!item.target_date) return false;
              return new Date(item.target_date) < now;
            }).length
          : null) ??
          observationSummary?.overdue_observations ??
          filteredChecklists.filter((item) => {
            if (isObservationFinal(item)) return false;
            if (!item.target_date) return false;
            return new Date(item.target_date) < now;
          }).length,
      )
    : 0;

  const onTimeClosuresCount = isObservations
    ? Number(
        (hasActiveDropdownFilters
          ? filteredChecklists.filter((item) => {
              if (statusOf(item) !== "approved") return false;
              if (!item.target_date || !item.updated_at) return false;
              return new Date(item.updated_at) <= new Date(item.target_date);
            }).length
          : null) ??
          observationSummary?.on_time_closures ??
          filteredChecklists.filter((item) => {
            if (statusOf(item) !== "approved") return false;
            if (!item.target_date || !item.updated_at) return false;
            return new Date(item.updated_at) <= new Date(item.target_date);
          }).length,
      )
    : 0;

  const matchesCardFilter = useCallback((item, filterKey) => {
    if (!filterKey || filterKey === "total") return true;
    const status = String(statusOf(item) || "").toLowerCase();

    switch (filterKey) {
      case "open observations":
        return !isObservationFinal(item);
      case "closed observations":
        return ["approved", "completed"].includes(status);
      case "total pending rectification":
      case "pending for rectification":
        return ["pending_maker", "pending_contractor_maker"].includes(status);
      case "pending for re-rectification":
        return (
          ["pending_maker", "pending_contractor_maker"].includes(status) &&
          Number(item.current_attempt_no || 0) > 1
        );
      case "unsafe act":
        return (
          String(item.unsafe_act_condition_category || "").toLowerCase() === "unsafe act"
        );
      case "unsafe condition":
        return (
          String(item.unsafe_act_condition_category || "").toLowerCase() === "unsafe condition"
        );
      case "low risk":
        return String(item.risk || "").toLowerCase().includes("low");
      case "medium risk":
        return String(item.risk || "").toLowerCase().includes("medium");
      case "high risk":
        return String(item.risk || "").toLowerCase().includes("high");
      case "overdue observations":
        return (
          !isObservationFinal(item) &&
          item.target_date &&
          new Date(item.target_date) < new Date()
        );
      case "on-time closures":
        return (
          status === "approved" &&
          item.target_date &&
          item.updated_at &&
          new Date(item.updated_at) <= new Date(item.target_date)
        );
      case "pending for review":
        return ["pending_checker", "pending_contractor_checker"].includes(status);
      case "approved":
        return isApprovedChecklist(item);
      case "rejected":
        return isRejectedChecklist(item);
      default:
        return true;
    }
  }, []);

  const [rectificationSubFilter, setRectificationSubFilter] = useState("all");

  const displayedCheckerPending = useMemo(() => {
    let list = checkerPending;
    if (activeCardFilter && activeCardFilter !== "total") {
      list = list.filter((item) => matchesCardFilter(item, activeCardFilter));
    }
    if (isObservations && rectificationSubFilter !== "all") {
      if (rectificationSubFilter === "initial") {
        list = list.filter((item) => Number(item.current_attempt_no || 1) <= 1);
      } else if (rectificationSubFilter === "re-rectification") {
        list = list.filter((item) => Number(item.current_attempt_no || 0) > 1);
      }
    }
    return list;
  }, [checkerPending, activeCardFilter, rectificationSubFilter, matchesCardFilter, isObservations]);

  const displayedCheckerAssigned = useMemo(() => {
    if (!activeCardFilter || activeCardFilter === "total") return checkerAssigned;
    return checkerAssigned.filter((item) => matchesCardFilter(item, activeCardFilter));
  }, [checkerAssigned, activeCardFilter, matchesCardFilter]);

  const displayedCheckerCompleted = useMemo(() => {
    if (!activeCardFilter || activeCardFilter === "total") return checkerCompleted;
    return checkerCompleted.filter((item) => matchesCardFilter(item, activeCardFilter));
  }, [checkerCompleted, activeCardFilter, matchesCardFilter]);

  const [rectificationPage, setRectificationPage] = useState(1);
  const [reviewPage, setReviewPage] = useState(1);

  useEffect(() => {
    setRectificationPage(1);
    setReviewPage(1);
    if (activeCardFilter === "pending for re-rectification") {
      setRectificationSubFilter("re-rectification");
    } else {
      setRectificationSubFilter("all");
    }
  }, [filters, activeCardFilter]);

  useEffect(() => {
    setRectificationPage(1);
  }, [rectificationSubFilter]);

  const totalRectificationCount = displayedCheckerPending.length;
  const totalRectificationPages = Math.max(1, Math.ceil(totalRectificationCount / 10));
  const currentRectificationPage = Math.min(Math.max(1, rectificationPage), totalRectificationPages);

  const paginatedCheckerPending = useMemo(() => {
    if (!isObservations) return displayedCheckerPending;
    const start = (currentRectificationPage - 1) * 10;
    return displayedCheckerPending.slice(start, start + 10);
  }, [displayedCheckerPending, currentRectificationPage, isObservations]);

  const totalReviewCount = displayedCheckerAssigned.length;
  const totalReviewPages = Math.max(1, Math.ceil(totalReviewCount / 10));
  const currentReviewPage = Math.min(Math.max(1, reviewPage), totalReviewPages);

  const paginatedCheckerAssigned = useMemo(() => {
    if (!isObservations) return displayedCheckerAssigned;
    const start = (currentReviewPage - 1) * 10;
    return displayedCheckerAssigned.slice(start, start + 10);
  }, [displayedCheckerAssigned, currentReviewPage, isObservations]);

  const observationPagination = useMemo(
    () => ({
      count: totalRectificationCount,
      total_pages: totalRectificationPages,
      current_page: currentRectificationPage,
      page_size: 10,
      next: currentRectificationPage < totalRectificationPages ? currentRectificationPage + 1 : null,
      previous: currentRectificationPage > 1 ? currentRectificationPage - 1 : null,
    }),
    [totalRectificationCount, totalRectificationPages, currentRectificationPage],
  );

  const observationPage = currentRectificationPage;
  const setObservationPage = setRectificationPage;

  const handleCardClick = (cardKey) => {
    if (cardKey === "total") {
      setActiveCardFilter(null);
      return;
    }
    setActiveCardFilter((prev) => (prev === cardKey ? null : cardKey));

    if (cardKey === "closed observations") {
      setTimeout(() => {
        const el = document.getElementById("closed-observations-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 50);
    }
  };

  const observationCounterCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total Raised Observations",
        icon: <ClipboardList className="h-4 w-4" />,
        color: "hsl(263, 83%, 53%)",
        count: checkerTotal,
      },
      {
        key: "open observations",
        label: "Open Observations",
        icon: <FolderOpen className="h-4 w-4" />,
        color: "hsl(210, 100%, 50%)",
        count: openObservationsCount,
      },
      {
        key: "closed observations",
        label: "Closed Observations",
        icon: <CheckCircle className="h-4 w-4" />,
        color: "hsl(145, 65%, 42%)",
        count: checkerApprovedCount,
      },
      {
        key: "total pending rectification",
        label: "Total Pending Rectification",
        icon: <Clock className="h-4 w-4" />,
        color: "hsl(45, 93%, 47%)",
        count: totalPendingRectificationCount,
        breakdown: `${pendingRectificationCount} Initial • ${pendingReRectificationCount} Re-Rect`,
      },
      {
        key: "pending for re-rectification",
        label: "Pending for Re-Rectification",
        icon: <Eye className="h-4 w-4" />,
        color: "hsl(24.6, 95%, 53.1%)",
        count: pendingReRectificationCount,
      },
      {
        key: "unsafe act",
        label: "Unsafe Act Observations",
        icon: <TriangleAlert className="h-4 w-4" />,
        color: "hsl(0, 75%, 55%)",
        count: unsafeActCount,
      },
      {
        key: "unsafe condition",
        label: "Unsafe Condition Observations",
        icon: <TriangleAlert className="h-4 w-4" />,
        color: "hsl(0, 75%, 55%)",
        count: unsafeConditionCount,
      },
      {
        key: "low risk",
        label: "Low Risk",
        icon: <Activity className="h-4 w-4" />,
        color: "hsl(145, 65%, 42%)",
        count: lowRiskCount,
      },
      {
        key: "medium risk",
        label: "Medium Risk",
        icon: <Activity className="h-4 w-4" />,
        color: "hsl(24.6, 95%, 53.1%)",
        count: mediumRiskCount,
      },
      {
        key: "high risk",
        label: "High Risk",
        icon: <Activity className="h-4 w-4" />,
        color: "hsl(0, 75%, 55%)",
        count: highRiskCount,
      },
      {
        key: "overdue observations",
        label: "Overdue Observations",
        icon: <Clock className="h-4 w-4" />,
        color: "hsl(0, 75%, 55%)",
        count: overdueObservationsCount,
      },
      {
        key: "on-time closures",
        label: "On-Time Closures",
        icon: <ClipboardCheck className="h-4 w-4" />,
        color: "hsl(145, 65%, 42%)",
        count: onTimeClosuresCount,
      },
    ],
    [
      checkerTotal,
      openObservationsCount,
      checkerApprovedCount,
      totalPendingRectificationCount,
      pendingRectificationCount,
      pendingReRectificationCount,
      unsafeActCount,
      unsafeConditionCount,
      lowRiskCount,
      mediumRiskCount,
      highRiskCount,
      overdueObservationsCount,
      onTimeClosuresCount,
    ],
  );

  const checklistCounterCards = useMemo(
    () => [
      {
        key: "pending for review",
        label: "Pending Review",
        icon: <Clock className="h-4 w-4" />,
        color: "hsl(45, 93%, 47%)",
        count: checkerAssigned.length,
      },
      {
        key: "pending for rectification",
        label: "Pending Rectification",
        icon: <Eye className="h-4 w-4" />,
        color: "hsl(24.6, 95%, 53.1%)",
        count: checkerPending.length,
      },
      {
        key: "approved",
        label: "Approved",
        icon: <CheckCircle className="h-4 w-4" />,
        color: "hsl(145, 65%, 42%)",
        count: checkerApprovedCount,
      },
      {
        key: "rejected",
        label: "Rejected",
        icon: <TriangleAlert className="h-4 w-4" />,
        color: "hsl(0, 75%, 55%)",
        count: checkerRejectedCount,
      },
      {
        key: "total",
        label: "Total",
        icon: <ClipboardList className="h-4 w-4" />,
        color: "hsl(263, 83%, 53%)",
        count: checkerTotal,
      },
    ],
    [
      checkerAssigned.length,
      checkerPending.length,
      checkerApprovedCount,
      checkerRejectedCount,
      checkerTotal,
    ],
  );

  const activeCardMeta = (
    isObservations ? observationCounterCards : checklistCounterCards
  ).find((c) => c.key === activeCardFilter);

  const totalMatchingActiveCard = useMemo(() => {
    if (!activeCardFilter || activeCardFilter === "total") return 0;
    return (
      displayedCheckerPending.length +
      displayedCheckerAssigned.length +
      displayedCheckerCompleted.length
    );
  }, [
    activeCardFilter,
    displayedCheckerPending.length,
    displayedCheckerAssigned.length,
    displayedCheckerCompleted.length,
  ]);


  const reportNameByDate = useMemo(() => {
    const dateSet = new Set();
    checklists.forEach((item) => {
      if (!item.created_at) return;
      const dateObj = new Date(item.created_at);
      const dateStr = dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      dateSet.add(dateStr);
    });
    const sortedDates = Array.from(dateSet).sort((a, b) => {
      const [d1, m1, y1] = a.split("/");
      const [d2, m2, y2] = b.split("/");
      return new Date(`${y1}-${m1}-${d1}`) - new Date(`${y2}-${m2}-${d2}`);
    });
    const mapping = {};
    sortedDates.forEach((dateStr, index) => {
      const reportNumber = String(index + 1).padStart(2, "0");
      mapping[dateStr] = `Report AN-ADL-SHOR-${reportNumber}`;
    });
    return mapping;
  }, [checklists]);

  const getObservationDisplayName = (item) => {
    const observationNo = item?.observation_no ?? item?.id;
    const reportNumber = item?.report_number || "";
    return reportNumber
      ? `Observation #${observationNo} / Report ${reportNumber}`
      : `Observation #${observationNo}`;
  };

  const getItemReportName = (item) => {
    if (!item.created_at) return "";
    const dateObj = new Date(item.created_at);
    const dateStr = dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    return reportNameByDate[dateStr] ? ` / ${reportNameByDate[dateStr]}` : "";
  };

  const getItemContractor = (item) => {
    const raw =
      item?.contractor_name ||
      item?.name_of_contractor ||
      item?.contractor?.name ||
      (typeof item?.contractor === "string" ? item?.contractor : "") ||
      item?.group_name ||
      item?.group?.name ||
      item?.assigned_group_name ||
      item?.safety_report_meta?.name_of_contractor ||
      item?.report_header_meta?.name_of_contractor ||
      "";
    const trimmed = String(raw || "").trim();
    if (!trimmed || trimmed === "—" || trimmed === "-" || trimmed.toLowerCase() === "internal flow") {
      return "Internal";
    }
    return trimmed;
  };

  const getItemInspectionReportNo = (item) => {
    if (isObservations) {
      return item?.report_number || "—";
    }

    const directNo = String(
      item?.inspection_report_no ||
        item?.safety_report_no ||
        item?.report_no ||
        item?.report_number ||
        item?.report_header_meta?.inspection_report_no ||
        item?.safety_report_meta?.inspection_report_no ||
        item?.report_meta?.inspection_report_no ||
        "",
    ).trim();

    if (directNo && !directNo.includes("AUTO")) {
      return directNo.replace(/^Report\s+/i, "");
    }

    if (item?.created_at) {
      const dateObj = new Date(item.created_at);
      const dateStr = dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      if (reportNameByDate && reportNameByDate[dateStr]) {
        return reportNameByDate[dateStr].replace(/^Report\s+/i, "");
      }
    }

    if (item?.id) {
      return `AN-ADL-SHOR-${String(item.id).padStart(2, "0")}`;
    }

    return "—";
  };

  const ChecklistListMeta = ({ item }) => {
    const contractor = getItemContractor(item);
    const reportNo = getItemInspectionReportNo(item);
    const wing = item?.wing || item?.tower_name || "Not Available";
    const rawCurrentlyWith = item?.currently_with;
    let currentlyWith = "Not Available";
    if (typeof rawCurrentlyWith === "string") {
      currentlyWith = rawCurrentlyWith.trim() || "Not Available";
    } else if (rawCurrentlyWith && typeof rawCurrentlyWith === "object") {
      const name = rawCurrentlyWith.name || rawCurrentlyWith.user_name || "";
      const desig = rawCurrentlyWith.designation || rawCurrentlyWith.group || "";
      if (name && desig) currentlyWith = `${name} (${desig})`;
      else if (name) currentlyWith = name;
      else if (desig) currentlyWith = desig;
    }

    const isInternal =
      !contractor ||
      contractor === "—" ||
      contractor === "-" ||
      contractor.toLowerCase() === "internal" ||
      contractor.toLowerCase() === "internal flow";
    const orgName = getInternalOrganizationName(item);

    return (
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <MetaChip
          icon={<UserRound className="h-3 w-3 shrink-0" />}
          label={isInternal ? null : "Contractor:"}
          value={isInternal ? orgName : contractor}
          tone="blue"
        />

        <MetaChip
          icon={<Building2 className="h-3 w-3 shrink-0" />}
          label="Wing:"
          value={wing}
          tone="slate"
        />

        <MetaChip
          icon={<FileText className="h-3 w-3 shrink-0" />}
          label="Report No.:"
          value={reportNo || "—"}
          tone="slate"
        />

        <MetaChip
          icon={<UserCheck className="h-3 w-3 shrink-0" />}
          label="Currently With:"
          value={currentlyWith}
          tone="slate"
        />
      </div>
    );
  };

  const groupedReports = useMemo(() => {
    const groups = {};
    const itemsToGroup = isObservations ? filteredChecklists : checkerCompleted;

    // Map of all observations by batch/report key to include all status items for that batch/day
    const allItemsByGroupKey = new Map();
    if (isObservations) {
      checklists.forEach((obs) => {
        if (!obs.created_at && !obs.report_date) return;
        const batchId = obs.report_batch_id || null;
        const reportNumber = obs.report_number || null;
        let dateStr;
        if (obs.report_date) {
          const parts = String(obs.report_date).split("-");
          dateStr = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : obs.report_date;
        } else {
          const dateObj = new Date(obs.created_at);
          dateStr = dateObj.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }
        const key = batchId ? `batch-${batchId}` : (reportNumber || dateStr);
        if (!allItemsByGroupKey.has(key)) {
          allItemsByGroupKey.set(key, []);
        }
        allItemsByGroupKey.get(key).push(obs);
      });
    }

    itemsToGroup.forEach((item) => {
      if (!item.created_at && !item.report_date) return;
      let groupKey;
      let dateStr;
      const reportNumber = item.report_number || null;
      const batchId = item.report_batch_id || null;

      if (isObservations) {
        if (item.report_date) {
          const parts = String(item.report_date).split("-");
          if (parts.length === 3) {
            const [y, m, d] = parts;
            dateStr = `${d}/${m}/${y}`;
          } else {
            dateStr = item.report_date;
          }
        } else {
          const dateObj = new Date(item.created_at);
          dateStr = dateObj.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }
        groupKey = batchId ? `batch-${batchId}` : (reportNumber || dateStr);
      } else {
        const dateObj = new Date(item.created_at);
        dateStr = dateObj.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
        groupKey = dateStr;
      }

      if (!groups[groupKey]) {
        const dateObj = item.created_at ? new Date(item.created_at) : new Date();
        const batchItems = isObservations && allItemsByGroupKey.has(groupKey)
          ? allItemsByGroupKey.get(groupKey)
          : [];

        groups[groupKey] = {
          date: dateStr,
          dateObj,
          reportBatchId: batchId,
          reportNumber: reportNumber,
          items: batchItems.length > 0 ? batchItems : [item],
          firstItemId: item.id,
        };
      }
      if (!groups[groupKey].reportBatchId && batchId) {
        groups[groupKey].reportBatchId = batchId;
      }
      if (!groups[groupKey].reportNumber && reportNumber) {
        groups[groupKey].reportNumber = reportNumber;
      }
      if (!isObservations) {
        groups[groupKey].items.push(item);
      }
    });

    const sortedDates = Object.keys(groups).sort((a, b) => {
      return groups[a].dateObj - groups[b].dateObj;
    });

    const reports = sortedDates
      .map((key) => {
        const grp = groups[key];
        let reportName;
        if (isObservations) {
          reportName = grp.reportNumber
            ? `Observation Report - ${grp.reportNumber}`
            : `Observation Reports - ${grp.date}`;
        } else {
          reportName = reportNameByDate[grp.date] || "Report Unknown";
        }
        return {
          ...grp,
          reportName,
        };
      })
      .reverse();

    return reports;
  }, [checklists, filteredChecklists, checkerCompleted, isObservations, reportNameByDate]);

  const checkerReviewItems =
    detail?.items
      ?.map((item) => {
        const sub = getPrimarySubmissionForCheckerView(item);

        return sub ? { item, sub } : null;
      })
      .filter(Boolean) ?? [];

  const pendingCheckerItems = checkerReviewItems;

  // ── navigation ──────────────────────────────────────────────
  const backToDashboard = () => {
    setView("dashboard");
    setDetail(null);
    // setInspectionAnswers({});
    // setVerificationAnswers({});
    setReviewerMedia({});
    setInspectionRemarks({});
    setVerificationRemarks({});
    setIsSignatureModalOpen(false);
    setPendingApprovePayload(null);
  };

  // ── open helpers ────────────────────────────────────────────
  const openAssignedInspection = async (cl) => {
    setDetail(null);
    setDetailLoading(true);
    // setInspectionAnswers({});
    setReviewerMedia({});
    setInspectionRemarks({});
    try {
      const res = await getSafetyChecklist(cl.id);
      const data = res?.data || null;
      setDetail(data);
      const initial = {};
      (data?.items || []).forEach((item) => {
        const sub = (item.submissions || []).find(
          (s) => s.status === "pending_checker",
        );
        if (sub) initial[sub.id] = null;
      });
      // setInspectionAnswers(initial);
      setView("inspection");
    } catch (err) {
      showToast(
        err?.response?.data?.detail || "Failed to load checklist",
        "error",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const openCheckerObservation = async (item) => {
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await getSafetyObservationById(item.id);
      setDetail(res?.data || null);
      setView("review_observation");
    } catch (err) {
      showToast(
        err?.response?.data?.detail || "Failed to load observation",
        "error",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const openPendingVerification = async (cl) => {
    setDetail(null);
    setDetailLoading(true);
    // setVerificationAnswers({});
    setReviewerMedia({});
    setVerificationRemarks({});
    try {
      const res = await getSafetyChecklist(cl.id);
      const data = res?.data || null;
      setDetail(data);
      const initial = {};
      (data?.items || []).forEach((entry) => {
        const match = (entry.submissions || []).find(
          (s) => s.status === "pending_checker",
        );
        if (
          match &&
          (match.maker_remarks ||
            match.has_photo ||
            (match.images || []).length)
        ) {
          initial[match.id] = "yes";
        }
      });
      // setVerificationAnswers(initial);
      setView("verification");
    } catch (err) {
      showToast(
        err?.response?.data?.detail || "Failed to load checklist",
        "error",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  // ── action handlers ─────────────────────────────────────────

  const formatChecklistDateTime = (value) => {
    if (!value) return "";

    const date = new Date(value);

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getObservationDisplayDateTime = (item) => {
    if (!item) return "";
    if (!isObservations) {
      return item.created_at ? formatChecklistDateTime(item.created_at) : "";
    }

    const latestAttempt =
      Array.isArray(item.attempts) && item.attempts.length > 0
        ? item.attempts[item.attempts.length - 1]
        : null;

    const dateVal =
      item.forwarded_at ||
      item.checker_reviewed_at ||
      latestAttempt?.checker_reviewed_at ||
      item.safety_officer_submitted_at ||
      latestAttempt?.safety_officer_submitted_at ||
      item.updated_at ||
      item.created_at;

    return dateVal ? formatChecklistDateTime(dateVal) : "";
  };

  const getPendingCheckerRows = () => checkerReviewItems;

  const handleSubmitChecklist = async () => {
    if (!detail) return;
    const submissions = [];
    let hasMissingAnswer = false;

    const currentStep = detail.current_assignee_step_index;
    const currentWA = detail.workflow_assignments?.find(
      (wa) => wa.order_index === currentStep,
    );
    const assignedQuestions = currentWA?.assigned_questions || [];

    checkerReviewItems.forEach(({ item, sub }, idx) => {
      const isAssignedHere =
        assignedQuestions.length === 0 || assignedQuestions.includes(idx);
      const needsFilling = isAssignedHere && !sub.maker_id;

      if (needsFilling) {
        const answer = checkerAnswers[sub.id] || "";
        if (!answer) {
          hasMissingAnswer = true;
        }
        submissions.push({
          submission_id: sub.id,
          answer: answer,
          maker_remarks: checkerRemarks[sub.id] || "",
        });
      }
    });

    if (submissions.length === 0) {
      showToast("No questions to submit.", "error");
      return;
    }

    if (hasMissingAnswer) {
      showToast("Please answer all required questions.", "error");
      return;
    }

    setPendingSubmitPayload({ submissions });
    setIsSubmitSignatureModalOpen(true);
  };

  const confirmSubmitChecklist = async (signatureFile) => {
    if (!pendingSubmitPayload) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append(
        "submissions",
        JSON.stringify(pendingSubmitPayload.submissions),
      );

      pendingSubmitPayload.submissions.forEach((sub) => {
        const file = checkerMedia[sub.submission_id];
        if (file) {
          formData.append(`checker_media_${sub.submission_id}`, file);
        }
      });

      if (signatureFile) {
        formData.append("maker_signature", signatureFile);
      }

      await submitSafetyChecklist(detail.id, formData);
      showToast("Questions submitted successfully", "success");
      setIsSubmitSignatureModalOpen(false);
      setPendingSubmitPayload(null);
      fetchList();
      setView("dashboard");
    } catch (error) {
      showToast("Failed to submit questions", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveChecklist = async () => {
    if (!detail) return;

    const rows = getPendingCheckerRows();

    if (!rows.length) {
      showToast("No pending questions found.", "error");
      return;
    }

    const formData = new FormData();

    formData.append(
      "submissions",
      JSON.stringify(
        rows.map(({ sub }) => ({
          submission_id: sub.id,
        })),
      ),
    );

    setPendingApprovePayload({ formData });
    setSignaturePurpose("approve");
    setIsSignatureModalOpen(true);
  };

  const handleCloseSignatureModal = () => {
    if (signatureSubmitting) return;
    setIsSignatureModalOpen(false);
    setPendingApprovePayload(null);
    if (sigCanvasRef.current) sigCanvasRef.current.clear();
  };

  const handleSubmitWithSignature = async () => {
    if (!detail) return;

    if (signaturePurpose === "approve" && !pendingApprovePayload) return;
    if (signaturePurpose === "reject" && !pendingRejectPayload) return;

    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      showToast("Please provide your signature.", "error");
      return;
    }

    const dataUrl = sigCanvasRef.current.toDataURL("image/png");
    const base64Part = dataUrl.split(",")[1] || "";

    if (!base64Part) {
      showToast("Could not capture signature. Try again.", "error");
      return;
    }

    const binary = atob(base64Part);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    const signatureFile = new File(
      [bytes],
      `checker-signature-${detail.id}-${Date.now()}.png`,
      { type: "image/png" },
    );

    setSignatureSubmitting(true);

    try {
      if (signaturePurpose === "reject") {
        const formData = pendingRejectPayload || new FormData();

        formData.append("checker_signature", signatureFile);

        await rejectSafetyChecklist(detail.id, formData);

        showToast("Checklist rejected and sent back to Maker.", "success");
      } else {
        const formData = pendingApprovePayload?.formData || new FormData();

        if (!formData.has("submissions")) {
          formData.append(
            "submissions",
            JSON.stringify(pendingApprovePayload?.submissions || []),
          );
        }

        formData.append("checker_signature", signatureFile);

        await approveSafetyChecklist(detail.id, formData);

        showToast("Checklist approved successfully.", "success");
      }

      setPendingRejectPayload(null);
      setPendingApprovePayload(null);
      setSignaturePurpose(null);

      handleCloseSignatureModal();
      backToDashboard();
      fetchList();
    } catch (err) {
      showToast(
        err?.response?.data?.detail ||
          (signaturePurpose === "reject"
            ? "Reject checklist failed."
            : "Signature submit failed"),
        "error",
      );
    } finally {
      setSignatureSubmitting(false);
    }
  };

  // For Mobile Responsive
  const isMobileDevice = () => {
    if (typeof navigator === "undefined") return false;

    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  const getAccessTokenForMobileDownload = () =>
    localStorage.getItem("ACCESS_TOKEN") ||
    localStorage.getItem("access") ||
    localStorage.getItem("accessToken") ||
    "";

  const getChecklistServiceBaseUrl = () => {
    const isLocal =
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "localhost";

    return isLocal
      ? "http://127.0.0.1:8001"
      : "https://konstruct.world/checklists";
  };

  const safePdfFileName = (name = "report") => {
    return (
      String(name || "report")
        .trim()
        .replace(/[^\w.-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "") || "report"
    );
  };

  const sendDownloadToFlutter = ({ url, fileName, token }) => {
    const payload = JSON.stringify({
      url,
      fileName,
      token,
      mimeType: "application/pdf",
    });

    // flutter_inappwebview handler
    if (window.flutter_inappwebview?.callHandler) {
      window.flutter_inappwebview.callHandler("downloadFile", payload);
      return true;
    }

    // webview_flutter JavaScriptChannel
    if (window.DownloadChannel?.postMessage) {
      window.DownloadChannel.postMessage(payload);
      return true;
    }

    return false;
  };

  // Old handleDownloadReport
  // const handleDownloadReport = async (item) => {
  //     if (!item?.id) return;

  //     if (isObservations && !item.skipGroupCheck) {
  //         const reportGroup = groupedReports.find(r => r.firstItemId === item.id);
  //         if (reportGroup) {
  //             showToast("Generating Observation Report PDF...", "success");
  //             try {
  //                 let orgId = null;
  //                 const userDataStr = localStorage.getItem("USER_DATA");
  //                 if (userDataStr) {
  //                     try {
  //                         orgId = JSON.parse(userDataStr).orgId;
  //                     } catch (e) { }
  //                 }
  //                 const projectId = resolveActiveProjectId?.() || resolveProjectIdFromUserAccess();
  //                 const res = await downloadSafetyObservationReport({
  //                     date: reportGroup.date,
  //                     org_id: orgId,
  //                     project_id: projectId,
  //                     t: new Date().getTime()
  //                 });

  //                 const blob = res?.data;
  //                 if (!blob) {
  //                     showToast("Report data is empty", "error");
  //                     return;
  //                 }
  //                 const url = window.URL.createObjectURL(new Blob([blob]));
  //                 const link = document.createElement("a");
  //                 link.href = url;

  //                 const safeName = reportGroup.reportName
  //                     ? reportGroup.reportName.replace(/[^a-zA-Z0-9_.-]/g, '_')
  //                     : `Observation-Report-${reportGroup.date}.pdf`;

  //                 link.setAttribute("download", `${safeName}.pdf`);
  //                 document.body.appendChild(link);
  //                 link.click();
  //                 link.parentNode.removeChild(link);
  //                 window.URL.revokeObjectURL(url);
  //                 return;
  //             } catch (err) {
  //                 console.error("PDF Gen Error:", err);
  //                 showToast("Failed to generate PDF", "error");
  //                 return;
  //             }
  //         }
  //     }

  //     try {
  //         const res = await downloadSafetyReport(item.id, {
  //             mode: "download",
  //         });

  //         const blob = res?.data;
  //         if (!blob) {
  //             showToast("Report file not found.", "error");
  //             return;
  //         }

  //         const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
  //         const link = document.createElement("a");

  //         const safeName = String(item.name || "safety-report")
  //             .trim()
  //             .replace(/[^\w.-]+/g, "-");

  //         link.href = url;
  //         link.download = `${safeName}-${item.id}.pdf`;
  //         document.body.appendChild(link);
  //         link.click();
  //         link.remove();

  //         window.URL.revokeObjectURL(url);
  //         showToast("Report downloaded", "success");
  //     } catch (err) {
  //         showToast(
  //             err?.response?.data?.detail || err?.message || "Download failed",
  //             "error"
  //         );
  //     }
  // };

  const handleDownloadReport = async (item) => {
    if (!item?.id) return;

    const mobile = isMobileDevice();
    const token = getAccessTokenForMobileDownload();
    const checklistBaseUrl = getChecklistServiceBaseUrl();

    /*
            MOBILE WEBVIEW FLOW:
            Do not use blob + a.download.
            Send real API URL to Flutter and let Flutter download using native code.
        */
    /*
      OBSERVATION REPORT FLOW (BOTH MOBILE & WEB):
    */
    if (isObservations && !item.skipGroupCheck) {
      let reportGroup = groupedReports.find(
        (r) =>
          (item.reportBatchId && r.reportBatchId === item.reportBatchId) ||
          (item.report_batch_id && r.reportBatchId === item.report_batch_id) ||
          (item.reportNumber && r.reportNumber === item.reportNumber) ||
          (item.report_number && r.reportNumber === item.report_number) ||
          r.firstItemId === item.id ||
          r.items?.some((entry) => entry?.id === item.id),
      );

      if (!reportGroup) {
        const matchingItems = (checklists || []).filter(
          (c) =>
            (item.report_batch_id && c.report_batch_id === item.report_batch_id) ||
            (item.report_number && c.report_number === item.report_number) ||
            c.id === item.id,
        );
        const reportItems = matchingItems.length > 0 ? matchingItems : [item];
        const first = reportItems[0] || item;
        const repNo = first.report_number || item.report_number || "";
        const repDate = formatReportDisplayDate(first.report_date || item.report_date, first.created_at || item.created_at);
        reportGroup = {
          reportBatchId: first.report_batch_id || item.report_batch_id || undefined,
          reportNumber: repNo || undefined,
          date: repDate,
          reportName: repNo
            ? `Observation Report - ${repNo}`
            : `Observation Report - ${repDate || "Report"}`,
          items: reportItems,
          firstItemId: first.id,
        };
      }

      if (reportGroup) {
        showToast("Generating Observation Report PDF...", "success");

        const firstItem = reportGroup.items?.[0] || {};
        const orgId = firstItem.org_id || resolveOrgIdFromSession() || null;
        const activeProjectId =
          firstItem.project_id ||
          resolveActiveProjectId?.() ||
          resolveProjectIdFromUserAccess();
        const groupedIds = (reportGroup.items || [])
          .map((entry) => entry?.id)
          .filter(Boolean);

        const fallbackName = `${safePdfFileName(
          reportGroup.reportName ||
            (reportGroup.reportNumber
              ? `Observation-Report-${reportGroup.reportNumber}`
              : `Observation-Report-${reportGroup.date || "Report"}`)
        )}.pdf`;

        // If on mobile device with native Flutter container, dispatch to Flutter
        if (mobile) {
          try {
            const query = new URLSearchParams({
              mode: "download",
            });
            if (reportGroup.reportBatchId) query.set("report_batch_id", reportGroup.reportBatchId);
            if (reportGroup.reportNumber) query.set("report_number", reportGroup.reportNumber);
            if (reportGroup.date) query.set("date", reportGroup.date);
            if (orgId) query.set("org_id", orgId);
            if (activeProjectId) query.set("project_id", activeProjectId);
            if (groupedIds.length > 0) query.set("ids", groupedIds.join(","));

            const reportUrl = `${checklistBaseUrl}/safety/observations/report/?${query.toString()}`;
            const sentToFlutter = sendDownloadToFlutter({
              url: reportUrl,
              fileName: fallbackName,
              token,
            });

            if (sentToFlutter) {
              showToast("Downloading report...", "success");
              return;
            }
          } catch (mobileErr) {
            console.error("Flutter download dispatch error:", mobileErr);
          }
        }

        // Web download flow (desktop or mobile browser)
        try {
          const res = await downloadSafetyObservationReport({
            report_batch_id: reportGroup.reportBatchId || undefined,
            report_number: reportGroup.reportNumber || undefined,
            date: reportGroup.date,
            org_id: orgId,
            project_id: activeProjectId,
            ids: groupedIds.join(","),
            t: new Date().getTime(),
          });

          const blob = res?.data;
          if (!blob || blob.size === 0) {
            showToast("Report data is empty", "error");
            return;
          }

          const contentType =
            res?.headers?.["content-type"] || blob?.type || "";
          if (contentType.includes("application/json")) {
            const message = await getBlobErrorMessage(
              res,
              "Failed to generate PDF",
            );
            showToast(message, "error");
            return;
          }

          const disposition = res?.headers?.["content-disposition"];
          const finalName = filenameFromDisposition(disposition) || fallbackName;

          downloadBlob(
            blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" }),
            finalName,
          );
          showToast("Report downloaded successfully", "success");
          return;
        } catch (err) {
          console.error("PDF Gen Error:", err);
          let errorMsg = "Failed to generate PDF";
          if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
            errorMsg = "Download timed out. Please try again.";
          } else {
            try {
              if (err?.response?.data instanceof Blob) {
                const text = await err.response.data.text();
                const parsed = JSON.parse(text);
                errorMsg = parsed?.detail || parsed?.message || errorMsg;
              } else if (err?.response?.data?.detail) {
                errorMsg = err.response.data.detail;
              } else if (err?.message) {
                errorMsg = err.message;
              }
            } catch {
              // fallback
            }
          }
          showToast(errorMsg, "error");
          return;
        }
      }
    }

    /*
      NORMAL SAFETY CHECKLIST REPORT MOBILE FLOW:
    */
    if (mobile) {
      try {
        const query = new URLSearchParams({
          mode: "download",
        });

        const reportUrl = `${checklistBaseUrl}/safety/checklists/${item.id}/report/?${query.toString()}`;
        const fileName = `${safePdfFileName(item.name || "safety-report")}-${item.id}.pdf`;

        const sentToFlutter = sendDownloadToFlutter({
          url: reportUrl,
          fileName,
          token,
        });

        if (sentToFlutter) {
          showToast("Downloading report...", "success");
          return;
        }

        window.open(reportUrl, "_blank");
        return;
      } catch (err) {
        console.error("Mobile download error:", err);
        showToast("Mobile download failed", "error");
        return;
      }
    }

    /*
      NORMAL SAFETY CHECKLIST REPORT DESKTOP FLOW:
    */

    try {
      const res = await downloadSafetyReport(item.id, {
        mode: "download",
      });

      const blob = res?.data;

      if (!blob) {
        showToast("Report file not found.", "error");
        return;
      }

      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" }),
      );

      const link = document.createElement("a");

      const safeName = safePdfFileName(item.name || "safety-report");

      link.href = url;
      link.download = `${safeName}-${item.id}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      showToast("Report downloaded", "success");
    } catch (err) {
      showToast(
        err?.response?.data?.detail || err?.message || "Download failed",
        "error",
      );
    }
  };

  const handleRejectChecklist = async () => {
    if (!detail) return;

    const rows = getPendingCheckerRows();

    const rejectedRows = rows.filter(({ sub }) => {
      const comment = String(checkerComments[sub.id] || "").trim();
      const file = reviewerMedia[sub.id];

      return comment || file;
    });

    if (!rejectedRows.length) {
      showToast(
        "Add comment or photo on at least one question before rejecting.",
        "error",
      );
      return;
    }

    const formData = new FormData();

    const submissionIds = rejectedRows.map(({ sub }) => sub.id);

    formData.append("submission_ids", JSON.stringify(submissionIds));

    formData.append(
      "rejections",
      JSON.stringify(
        rejectedRows.map(({ sub }) => ({
          submission_id: sub.id,
          checker_remarks: checkerComments[sub.id] || "",
        })),
      ),
    );

    rejectedRows.forEach(({ sub }) => {
      const file = reviewerMedia[sub.id];

      if (file) {
        formData.append(`reviewer_media_${sub.id}`, file);
      }
    });

    setSubmitting(true);

    try {
      setPendingRejectPayload(formData);
      setSignaturePurpose("reject");
      setIsSignatureModalOpen(true);
      return;

      showToast("Checklist rejected and sent back to Maker.", "success");

      backToDashboard();
      fetchList();
    } catch (err) {
      showToast(
        err?.response?.data?.detail || "Reject checklist failed.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── render ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50/60 p-4 sm:p-6 lg:p-8">
      <div
        className={`mx-auto w-full ${
          view === "dashboard" ? "max-w-[1600px] space-y-6" : "max-w-5xl"
        }`}
      >
        {!userId && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            User not found in session. Log in or ensure USER_DATA is set in
            localStorage.
          </div>
        )}

        {loading && (
          <div className="py-8 text-center text-muted-foreground">Loading…</div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ═══ VIEW: INSPECTION ═══ */}
            {view === "inspection" && detail && (
              <section>
                <button
                  type="button"
                  onClick={backToDashboard}
                  className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to Dashboard
                </button>

                <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
                  <h1 className="text-lg font-bold text-foreground sm:text-xl">
                    {detail.name}
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {(detail.items || []).length} questions
                  </p>
                </div>

                <ReportHeaderInfoCard detail={detail} />

                <div className="space-y-4">
                  {checkerReviewItems.map(({ item, sub }, idx) => {
                    const isResubmitted = isResubmittedForReview(sub);

                    const currentStep = detail.current_assignee_step_index;
                    const currentWA = detail.workflow_assignments?.find(
                      (wa) => wa.order_index === currentStep,
                    );
                    const assignedQuestions =
                      currentWA?.assigned_questions || [];
                    const isAssignedHere =
                      assignedQuestions.length === 0 ||
                      assignedQuestions.includes(idx);
                    const hasAnswer = Boolean(
                      sub.answer ||
                      sub.latest_maker_answer ||
                      sub.latest_checker_answer,
                    );
                    const needsFilling =
                      isAssignedHere &&
                      (!hasAnswer || sub.status === "created");
                    const isHidden = !isAssignedHere && !hasAnswer;

                    if (isHidden) return null;

                    const makerComment =
                      sub.latest_maker_remarks || sub.maker_remarks || "";

                    const makerPhoto =
                      sub.latest_maker_photo_url || sub.photo_url || "";

                    return (
                      <div
                        key={item.id}
                        className={`rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md ${
                          isResubmitted
                            ? "border-yellow-400 bg-yellow-50"
                            : "border-border bg-card"
                        }`}
                      >
                        {isResubmitted && (
                          <span className="mb-3 inline-flex rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
                            Re-submitted by {getResubmittedMakerName(sub)}
                          </span>
                        )}

                        <div className="mb-4 flex items-start gap-3">
                          <QuestionBadge number={idx + 1} />
                          <p className="pt-1 text-sm font-semibold text-foreground sm:text-base">
                            {item.title}
                          </p>
                        </div>

                        <div className="space-y-3">
                          {needsFilling ? (
                            <>
                              <TemplateOptionButtons
                                options={item.options}
                                value={checkerAnswers[sub.id]}
                                onChange={(value) =>
                                  setCheckerAnswers((prev) => ({
                                    ...prev,
                                    [sub.id]: value,
                                  }))
                                }
                              />
                              <div>
                                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                  Remark / Comment
                                </label>
                                <textarea
                                  rows={1}
                                  value={checkerRemarks[sub.id] || ""}
                                  onChange={(e) => {
                                    setCheckerRemarks((prev) => ({
                                      ...prev,
                                      [sub.id]: e.target.value,
                                    }));
                                    autoResizeTextarea(e.target);
                                  }}
                                  onInput={(e) => autoResizeTextarea(e.target)}
                                  className="min-h-[42px] w-full resize-none overflow-hidden rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                                  placeholder="Add a remark..."
                                />
                              </div>
                              <PhotoUploadArea
                                id={`checker-media-${sub.id}`}
                                previewBase64={checkerMedia[sub.id]}
                                label={
                                  item.photo_required
                                    ? "Upload photo (Required)"
                                    : "Upload photo (Optional)"
                                }
                                onFileChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file)
                                    setCheckerMedia((prev) => ({
                                      ...prev,
                                      [sub.id]: file,
                                    }));
                                }}
                                onRemove={() =>
                                  setCheckerMedia((prev) => ({
                                    ...prev,
                                    [sub.id]: undefined,
                                  }))
                                }
                              />
                            </>
                          ) : (
                            <>
                              <div>
                                <ReadOnlyOptionButtons
                                  options={item.options}
                                  value={sub.latest_maker_answer}
                                />
                              </div>

                              {makerComment && (
                                <RemarksBubble
                                  label="Maker Comment"
                                  text={makerComment}
                                />
                              )}

                              <div className="pt-1">
                                {makerPhoto ? (
                                  <PhotoViewButton
                                    url={makerPhoto}
                                    label="View Maker Photo"
                                  />
                                ) : (
                                  <NoPhotoLabel />
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <SafetyChecklistSignOffSection
                  detail={detail}
                  className="mt-6"
                />

                {checkerReviewItems.some(({ sub }, idx) => {
                  const currentStep = detail.current_assignee_step_index;
                  const currentWA = detail.workflow_assignments?.find(
                    (wa) => wa.order_index === currentStep,
                  );
                  const assignedQuestions = currentWA?.assigned_questions || [];
                  const isAssignedHere =
                    assignedQuestions.length === 0 ||
                    assignedQuestions.includes(idx);
                  const hasAnswer = Boolean(
                    sub.answer ||
                    sub.latest_maker_answer ||
                    sub.latest_checker_answer,
                  );
                  return (
                    isAssignedHere && (!hasAnswer || sub.status === "created")
                  );
                }) ? (
                  <div className="mt-6 grid grid-cols-1 gap-3">
                    <button
                      type="button"
                      onClick={handleSubmitChecklist}
                      disabled={submitting}
                      className="flex w-full items-center justify-center rounded-xl bg-orange-500 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : "Submit Checklist"}
                    </button>
                  </div>
                ) : (
                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={handleRejectChecklist}
                      disabled={submitting}
                      className="flex w-full items-center justify-center rounded-xl bg-red-500 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : "Reject Checklist"}
                    </button>

                    <button
                      type="button"
                      onClick={handleApproveChecklist}
                      disabled={submitting}
                      className="flex w-full items-center justify-center rounded-xl bg-green-600 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : "Approve Checklist"}
                    </button>
                  </div>
                )}
              </section>
            )}

            {/* ═══ VIEW: VERIFICATION ═══ */}
            {view === "verification" && detail && (
              <section>
                <button
                  type="button"
                  onClick={backToDashboard}
                  className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to Dashboard
                </button>

                <div className="mb-6 flex items-center justify-between rounded-xl border bg-card p-5 shadow-sm">
                  <div>
                    <p className="text-base font-bold text-foreground sm:text-lg">
                      {detail.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {checkerReviewItems.length} item(s) to verify
                    </p>
                  </div>
                  <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-600">
                    Fix Submitted
                  </span>
                </div>

                <ReportHeaderInfoCard detail={detail} />

                <div className="space-y-4">
                  {checkerReviewItems.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      No items found for verification.
                    </p>
                  ) : (
                    checkerReviewItems.map(({ item, sub }, idx) => {
                      const isResubmitted = isResubmittedForReview(sub);

                      const makerComment =
                        sub.latest_maker_remarks || sub.maker_remarks || "";

                      const makerPhoto =
                        sub.latest_maker_photo_url || sub.photo_url || "";

                      return (
                        <div
                          key={sub.id}
                          className={`rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md ${
                            isResubmitted
                              ? "border-yellow-400 bg-yellow-50"
                              : "border-border bg-card"
                          }`}
                        >
                          {isResubmitted && (
                            <span className="mb-3 inline-flex rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
                              Re-submitted by {getResubmittedMakerName(sub)}
                            </span>
                          )}

                          <div className="mb-4 flex items-start gap-3">
                            <QuestionBadge number={idx + 1} />
                            <p className="pt-1 text-sm font-semibold text-foreground sm:text-base">
                              {item.title}
                            </p>
                          </div>

                          <div className="space-y-3">
                            <div>
                              {/* <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                                                                Maker Answer
                                                            </p> */}

                              <ReadOnlyOptionButtons
                                options={item.options}
                                value={sub.latest_maker_answer}
                              />
                            </div>

                            {sub.latest_checker_reject_remarks && (
                              <RemarksBubble
                                label="Previous Checker Objection"
                                text={sub.latest_checker_reject_remarks}
                              />
                            )}

                            {makerComment && (
                              <RemarksBubble
                                label="Maker Comment"
                                text={makerComment}
                              />
                            )}

                            {sub.supervisor_remarks && (
                              <RemarksBubble
                                label="Supervisor"
                                text={sub.supervisor_remarks}
                              />
                            )}

                            {sub.latest_checker_reject_photo_url && (
                              <PhotoViewButton
                                url={sub.latest_checker_reject_photo_url}
                                label="View Previous Checker Photo"
                              />
                            )}

                            <div className="pt-1">
                              {makerPhoto ? (
                                <PhotoViewButton
                                  url={makerPhoto}
                                  label="View Maker Photo"
                                />
                              ) : (
                                <NoPhotoLabel />
                              )}
                            </div>

                            <div>
                              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                Checker Comment / Objection
                              </label>

                              <textarea
                                rows={1}
                                value={checkerComments[sub.id] || ""}
                                onChange={(e) => {
                                  setCheckerComments((prev) => ({
                                    ...prev,
                                    [sub.id]: e.target.value,
                                  }));

                                  autoResizeTextarea(e.target);
                                }}
                                onInput={(e) => autoResizeTextarea(e.target)}
                                className="min-h-[42px] w-full resize-none overflow-hidden rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                                placeholder="Add comment if you want to reject this question..."
                              />
                            </div>

                            <PhotoUploadArea
                              id={`checker-objection-${sub.id}`}
                              previewBase64={reviewerMedia[sub.id]}
                              label="Upload objection photo (optional)"
                              onFileChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;

                                setReviewerMedia((prev) => ({
                                  ...prev,
                                  [sub.id]: file,
                                }));
                              }}
                              onRemove={() =>
                                setReviewerMedia((prev) => ({
                                  ...prev,
                                  [sub.id]: undefined,
                                }))
                              }
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <SafetyChecklistSignOffSection
                  detail={detail}
                  className="mt-6"
                />

                {pendingCheckerItems.length > 0 && (
                  <div className="mt-6">
                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={handleRejectChecklist}
                        disabled={submitting}
                        className="flex w-full items-center justify-center rounded-xl bg-red-500 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-60"
                      >
                        {submitting ? "Submitting..." : "Reject Checklist"}
                      </button>

                      <button
                        type="button"
                        onClick={handleApproveChecklist}
                        disabled={submitting}
                        className="flex w-full items-center justify-center rounded-xl bg-green-600 px-4 py-3.5 text-sm font-bold text-white disabled:opacity-60"
                      >
                        {submitting ? "Submitting..." : "Approve Checklist"}
                      </button>
                    </div>
                  </div>
                )}
</section>
            )}

            {/* ═══ VIEW: CREATE ═══ */}
            {view === "create" &&
              (window.location.pathname.includes("/safety/observations") ? (
                <CreateSafetyObservation
                  onBack={() => {
                    setView("dashboard");
                    fetchList();
                  }}
                />
              ) : (
                <SafetyChecklistCreateView
                  onBack={() => setView("dashboard")}
                  allowedFirstRoles={["CHECKER", "INITIALIZER"]}
                />
              ))}

            {/* ═══ VIEW: REVIEW OBSERVATION ═══ */}
            {view === "review_observation" && detail && (
              <ReviewSafetyObservation
                detail={detail}
                onBack={() => setView("dashboard")}
                onSuccess={() => {
                  setView("dashboard");
                  fetchList();
                }}
              />
            )}

            {/* ═══ VIEW: DASHBOARD ═══ */}
            {view === "dashboard" && (
              <div className="w-full space-y-6">
                <section className="space-y-6">
                  {/* Header */}
                  {(() => {
                    const activeWorkflowRole = normalizeRole(
                      localStorage.getItem("FLOW_ROLE") ||
                        localStorage.getItem("ROLE") ||
                        "",
                    );
                    const canRaiseObservation =
                      CHECKER_WORKFLOW_ROLES.has(activeWorkflowRole);

                    return (
                      <SafetyHeroBanner
                        type={isObservations ? "observation" : "checklist"}
                        categoryTag={isObservations ? "HAZARD & OBSERVATION TRACKING" : "SAFETY ASSURANCE"}
                        categoryIcon={isObservations ? TriangleAlert : ShieldCheck}
                        title={isObservations ? "Safety Observations" : "Safety Checklists"}
                        subtitle={
                          isObservations
                            ? "Site safety observations and hazard tracking — identify risks and log corrective actions."
                            : "Safety checklist management and audits — review, verify, and enforce protocols."
                        }
                      >
                        {isObservations && canRaiseObservation && (
                          <button
                            type="button"
                            onClick={() => setView("create")}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm hover:shadow-md hover:from-orange-600 hover:to-orange-700 transition-all cursor-pointer"
                          >
                            <Plus className="h-4 w-4" />
                            Raise Observation
                          </button>
                        )}
                      </SafetyHeroBanner>
                    );
                  })()}

                  {/* Counter cards */}
                  <div
                    className={
                      isObservations
                        ? "grid grid-cols-2 gap-3.5 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6"
                        : "grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-5"
                    }
                  >
                    {(isObservations
                      ? observationCounterCards
                      : checklistCounterCards
                    ).map(({ key, label, icon, color, count, breakdown }) => {
                      const isCardActive = activeCardFilter === key;

                      return (
                        <div
                          key={key}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleCardClick(key)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleCardClick(key);
                            }
                          }}
                          className={`group relative flex flex-col justify-between h-full rounded-2xl border bg-card p-3.5 sm:p-4 transition-all duration-200 select-none cursor-pointer hover:-translate-y-0.5 hover:shadow-md ${
                            isCardActive
                              ? "ring-2 shadow-sm"
                              : "border-slate-200/90 shadow-2xs hover:border-slate-300"
                          }`}
                          style={
                            isCardActive
                              ? {
                                  borderColor: color,
                                  ringColor: color,
                                  backgroundColor: `${color}08`,
                                  boxShadow: `0 0 0 2px ${color}35, 0 4px 12px ${color}15`,
                                }
                              : undefined
                          }
                        >
                          {isCardActive && (
                            <span
                              className="absolute top-2 right-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-2xs"
                              style={{ backgroundColor: color }}
                            >
                              Active
                            </span>
                          )}

                          {/* Top: Icon Badge + Label with fixed min-height for uniform alignment */}
                          <div className="flex items-start gap-2.5 min-h-[38px]">
                            <span
                              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors"
                              style={{
                                backgroundColor: `${color}18`,
                                color: color,
                              }}
                            >
                              {icon}
                            </span>
                            <span
                              className="text-xs sm:text-[13px] font-semibold leading-snug line-clamp-2"
                              style={{ color: color }}
                            >
                              {label}
                            </span>
                          </div>

                          {/* Bottom: Count strictly aligned to horizontal baseline */}
                          <div className="mt-3 flex flex-wrap items-baseline justify-between gap-1 border-t border-border/40 pt-2.5">
                            <span className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                              {Number.isFinite(Number(count)) ? Number(count) : 0}
                            </span>
                            {breakdown && (
                              <span className="text-[10px] font-semibold text-amber-800/90 bg-amber-500/10 rounded-md px-1.5 py-0.5 whitespace-nowrap">
                                {breakdown}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Active filter banner */}
                  {activeCardFilter && activeCardMeta && (
                    <div className="mt-3.5 flex flex-wrap items-center justify-between gap-2.5 rounded-xl border border-slate-200/90 bg-slate-50/90 px-4 py-2.5 shadow-2xs">
                      <div className="flex items-center gap-2">
                        <span
                          className="flex h-6 w-6 items-center justify-center rounded-lg"
                          style={{
                            backgroundColor: `${activeCardMeta.color}20`,
                            color: activeCardMeta.color,
                          }}
                        >
                          {activeCardMeta.icon}
                        </span>
                        <span className="text-xs sm:text-sm font-semibold text-foreground">
                          Active Filter:{" "}
                          <span style={{ color: activeCardMeta.color }}>
                            {activeCardMeta.label}
                          </span>
                        </span>
                        <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-muted-foreground shadow-2xs border border-border/60">
                          {totalMatchingActiveCard} item{totalMatchingActiveCard === 1 ? "" : "s"} visible
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveCardFilter(null)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300/80 bg-white px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-slate-100 transition-colors cursor-pointer"
                      >
                        <XIcon className="h-3.5 w-3.5" />
                        Reset Filter
                      </button>
                    </div>
                  )}

                  {/* Active Action Queues: Side-by-side 2-column grid */}
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {/* Left Column: Pending for Rectification */}
                    <div className="flex flex-col">
                      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-yellow-500/15 text-yellow-600">
                            <Clock className="h-4 w-4" />
                          </div>
                          <h2 className="text-sm font-semibold text-yellow-700 sm:text-base">
                            Pending for Rectification
                          </h2>
                          <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800">
                            {displayedCheckerPending.length}
                          </span>
                        </div>

                        {isObservations && (
                          <div className="flex items-center gap-1 rounded-xl bg-slate-100/90 p-1 text-[11px] font-medium border border-slate-200/80">
                            <button
                              type="button"
                              onClick={() => {
                                if (activeCardFilter === "pending for re-rectification") {
                                  setActiveCardFilter(null);
                                }
                                setRectificationSubFilter("all");
                              }}
                              className={`rounded-lg px-2 py-0.5 transition-all cursor-pointer ${
                                rectificationSubFilter === "all"
                                  ? "bg-white text-yellow-800 font-bold shadow-2xs"
                                  : "text-slate-600 hover:text-foreground"
                              }`}
                              title="Show all pending rectifications"
                            >
                              All ({totalPendingRectificationCount})
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (activeCardFilter === "pending for re-rectification") {
                                  setActiveCardFilter(null);
                                }
                                setRectificationSubFilter("initial");
                              }}
                              className={`rounded-lg px-2 py-0.5 transition-all cursor-pointer ${
                                rectificationSubFilter === "initial"
                                  ? "bg-white text-yellow-800 font-bold shadow-2xs"
                                  : "text-slate-600 hover:text-foreground"
                              }`}
                              title="Show first-attempt pending rectifications"
                            >
                              Initial ({pendingRectificationCount})
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setRectificationSubFilter("re-rectification");
                              }}
                              className={`rounded-lg px-2 py-0.5 transition-all cursor-pointer ${
                                rectificationSubFilter === "re-rectification"
                                  ? "bg-white text-orange-700 font-bold shadow-2xs"
                                  : "text-slate-600 hover:text-foreground"
                              }`}
                              title="Show re-rectifications (attempt 2+)"
                            >
                              Re-Rect ({pendingReRectificationCount})
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[460px] custom-scrollbar divide-y divide-border/50">
                        {displayedCheckerPending.length === 0 ? (
                          <div className="flex flex-col h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground gap-1.5">
                            <span>
                              {activeCardFilter
                                ? `No pending rectifications matching "${activeCardMeta?.label || activeCardFilter}"`
                                : "No pending rectifications"}
                            </span>
                            {activeCardFilter && (
                              <button
                                type="button"
                                onClick={() => setActiveCardFilter(null)}
                                className="text-xs font-semibold text-primary hover:underline cursor-pointer"
                              >
                                Clear filter
                              </button>
                            )}
                          </div>
                        ) : (
                          (isObservations ? paginatedCheckerPending : displayedCheckerPending).map((item) => {
                            const canReview = checkerCanReview(item);

                            return (
                              <div
                                key={item.id}
                                className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                    {isObservations
                                      ? getObservationDisplayName(item)
                                      : `${item.name || `Observation #${item.id}`}${getItemReportName(item)}`}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {getObservationDisplayDateTime(item)}
                                  </p>
                                  <ChecklistListMeta item={item} />
                                </div>

                                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                                  <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                      isObservations && Number(item.current_attempt_no || 1) > 1
                                        ? "bg-orange-100 text-orange-800 border border-orange-200"
                                        : "bg-yellow-100 text-yellow-700"
                                    }`}
                                  >
                                    {isObservations
                                      ? Number(item.current_attempt_no || 1) > 1
                                        ? `Pending Re-Rectification (Attempt #${item.current_attempt_no})`
                                        : "Pending Rectification"
                                      : canReview
                                        ? "Pending Rectification"
                                        : "Rectifying"}
                                  </span>

                                  {isObservations ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setReadonlyChecklist(item);
                                        setReadonlyPendingStatusLabel(
                                          "Pending for Rectification",
                                        );
                                      }}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 cursor-pointer"
                                      title="View Details"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                  ) : canReview ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openPendingVerification(item)
                                      }
                                      className="rounded-xl bg-gradient-to-r from-orange-400 to-orange-500 px-3.5 py-1.5 text-xs font-semibold text-white shadow-2xs transition-all hover:shadow-md cursor-pointer"
                                    >
                                      Review Rectifications
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setReadonlyChecklist(item);
                                        setReadonlyPendingStatusLabel(
                                          "Pending for Rectification",
                                        );
                                      }}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 cursor-pointer"
                                      title="View Details"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      {isObservations && totalRectificationPages > 1 && (
                        <div className="mt-2.5 flex items-center justify-between px-1 text-xs text-muted-foreground">
                          <span>
                            Page {currentRectificationPage} of {totalRectificationPages} ({totalRectificationCount} total)
                          </span>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setRectificationPage((prev) => Math.max(1, prev - 1))}
                              disabled={currentRectificationPage <= 1}
                              className="rounded-lg border border-border bg-white px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="inline-flex min-w-6 items-center justify-center rounded bg-orange-50 px-2 py-0.5 text-xs font-bold text-orange-600">
                              {currentRectificationPage}
                            </span>
                            <button
                              type="button"
                              onClick={() => setRectificationPage((prev) => Math.min(totalRectificationPages, prev + 1))}
                              disabled={currentRectificationPage >= totalRectificationPages}
                              className="rounded-lg border border-border bg-white px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column: Pending for Review / Re-Rectification */}
                    <div className="flex flex-col">
                      <div className="mb-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-500/15 text-orange-600">
                            <Eye className="h-4 w-4" />
                          </div>
                          <h2 className="text-sm font-semibold text-orange-700 sm:text-base">
                            Pending for Review
                          </h2>
                          <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-800">
                            {displayedCheckerAssigned.length}
                          </span>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[460px] custom-scrollbar divide-y divide-border/50">
                        {displayedCheckerAssigned.length === 0 ? (
                          <div className="flex flex-col h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground gap-1.5">
                            <span>
                              {activeCardFilter
                                ? `No items pending review matching "${activeCardMeta?.label || activeCardFilter}"`
                                : "No items pending review"}
                            </span>
                            {activeCardFilter && (
                              <button
                                type="button"
                                onClick={() => setActiveCardFilter(null)}
                                className="text-xs font-semibold text-primary hover:underline cursor-pointer"
                              >
                                Clear filter
                              </button>
                            )}
                          </div>
                        ) : (
                          (isObservations ? paginatedCheckerAssigned : displayedCheckerAssigned).map((item) => (
                            <div
                              key={item.id}
                              className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                  {isObservations
                                    ? getObservationDisplayName(item)
                                    : `${item.name || `Observation #${item.id}`}${getItemReportName(item)}`}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {getObservationDisplayDateTime(item)}
                                </p>
                                <ChecklistListMeta item={item} />
                              </div>
                              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                                <span
                                  className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-orange-100 text-orange-700"
                                >
                                  {isObservations
                                    ? checkerCanReview(item)
                                      ? isObservationRecertification(item)
                                        ? "Review Recertification"
                                        : "Pending For Review"
                                      : "In Review"
                                    : "Pending for Review"}
                                </span>
                                {isObservations ? (
                                  checkerCanReview(item) ? (
                                    <button
                                      type="button"
                                      onClick={() => openCheckerObservation(item)}
                                      className="rounded-xl bg-gradient-to-r from-orange-400 to-orange-500 px-3.5 py-1.5 text-xs font-semibold text-white shadow-2xs transition-all hover:shadow-md cursor-pointer"
                                    >
                                      {isObservationRecertification(item)
                                        ? "Review Recertified Observation"
                                        : "Review Observation"}
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setReadonlyChecklist(item);
                                        setReadonlyPendingStatusLabel(
                                          "Pending for Review",
                                        );
                                      }}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 cursor-pointer"
                                      title="View Details"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                  )
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => openAssignedInspection(item)}
                                    className="rounded-xl bg-gradient-to-r from-orange-400 to-orange-500 px-3.5 py-1.5 text-xs font-semibold text-white shadow-2xs transition-all hover:shadow-md cursor-pointer"
                                  >
                                    Review Checklist
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      {isObservations && totalReviewPages > 1 && (
                        <div className="mt-2.5 flex items-center justify-between px-1 text-xs text-muted-foreground">
                          <span>
                            Page {currentReviewPage} of {totalReviewPages} ({totalReviewCount} total)
                          </span>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setReviewPage((prev) => Math.max(1, prev - 1))}
                              disabled={currentReviewPage <= 1}
                              className="rounded-lg border border-border bg-white px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="inline-flex min-w-6 items-center justify-center rounded bg-orange-50 px-2 py-0.5 text-xs font-bold text-orange-600">
                              {currentReviewPage}
                            </span>
                            <button
                              type="button"
                              onClick={() => setReviewPage((prev) => Math.min(totalReviewPages, prev + 1))}
                              disabled={currentReviewPage >= totalReviewPages}
                              className="rounded-lg border border-border bg-white px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <ChecklistFilters
                    filters={filters}
                    setFilters={setFilters}
                    contractorOptions={contractorOptions}
                    typeOptions={typeOptions}
                    isObservations={isObservations}
                    wingOptions={wingOptions}
                    hazardOptions={hazardOptions}
                    checkerOptions={checkerOptions}
                    checkerLoading={checkerLoading}
                  />

                  {/* Completed / Closed Observations */}
                  {isObservations ? (
                    <div id="closed-observations-section" className="grid grid-cols-1 gap-6 md:grid-cols-2 mb-6">
                      {/* Left Side: Closed Observations */}
                      <div className="flex flex-col">
                        <div className="mb-2.5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-600">
                              <CheckCircle className="h-4 w-4" />
                            </div>
                            <h2 className="text-sm font-semibold text-emerald-700 sm:text-base">
                              Closed Observations
                            </h2>
                            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                              {displayedCheckerCompleted.length}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[420px] custom-scrollbar divide-y divide-border/50">
                          {displayedCheckerCompleted.length === 0 ? (
                            <div className="flex flex-col h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground gap-1.5">
                              <span>
                                {activeCardFilter
                                  ? `No closed observations matching "${activeCardMeta?.label || activeCardFilter}"`
                                  : "No closed observations"}
                              </span>
                              {activeCardFilter && (
                                <button
                                  type="button"
                                  onClick={() => setActiveCardFilter(null)}
                                  className="text-xs font-semibold text-primary hover:underline cursor-pointer"
                                >
                                  Clear filter
                                </button>
                              )}
                            </div>
                          ) : (
                            displayedCheckerCompleted.map((item) => (
                              <div
                                key={item.id}
                                className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                    {isObservations
                                      ? getObservationDisplayName(item)
                                      : `${item.name || `Observation #${item.id}`}${getItemReportName(item)}`}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {getObservationDisplayDateTime(item)}
                                  </p>
                                  <ChecklistListMeta item={item} />
                                </div>
                                <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                                  <button
                                    type="button"
                                    title="View History"
                                    onClick={() => {
                                      setReadonlyChecklist(item);
                                      setReadonlyPendingStatusLabel(
                                        "Closed Observation",
                                      );
                                    }}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 cursor-pointer"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  {isObservations && (
                                    <button
                                      type="button"
                                      title="Download Report"
                                      onClick={() => handleDownloadReport(item)}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-green-200 bg-green-50 text-green-700 transition-colors hover:bg-green-100 cursor-pointer"
                                    >
                                      <Download className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Right Side: Reports */}
                      <div className="flex flex-col">
                        <div className="mb-2.5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-600">
                              <CheckCircle className="h-4 w-4" />
                            </div>
                            <h2 className="text-sm font-semibold text-emerald-700 sm:text-base">
                              Reports
                            </h2>
                            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                              {groupedReports.length}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[420px] custom-scrollbar divide-y divide-border/50">
                          {groupedReports.length === 0 ? (
                            <div className="flex h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                              No reports generated
                            </div>
                          ) : (
                            groupedReports.map((report) => (
                              <div
                                key={`report-${report.date}`}
                                className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                    {report.reportName}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {report.date}
                                  </p>
                                </div>
                                <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                                  <button
                                    type="button"
                                    title="Download Report"
                                    onClick={() =>
                                      handleDownloadReport({
                                        id: report.firstItemId,
                                        name: report.reportName,
                                        date: report.date,
                                        reportBatchId: report.reportBatchId,
                                        reportNumber: report.reportNumber,
                                      })
                                    }
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-green-200 bg-green-50 text-green-700 transition-colors hover:bg-green-100 cursor-pointer"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 flex flex-col">
                      <div className="mb-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-600">
                            <CheckCircle className="h-4 w-4" />
                          </div>
                          <h2 className="text-sm font-semibold text-emerald-700 sm:text-base">
                            Completed Checklists
                          </h2>
                          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                            {displayedCheckerCompleted.length}
                          </span>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[420px] custom-scrollbar divide-y divide-border/50">
                        {checkerCompleted.length === 0 ? (
                          <div className="flex h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                            No completed checklists
                          </div>
                        ) : (
                          checkerCompleted.map((item) => (
                            <div
                              key={item.id}
                              className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                  {item.name}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {item.created_at
                                    ? formatChecklistDateTime(item.created_at)
                                    : ""}
                                </p>
                                <ChecklistListMeta item={item} />
                              </div>

                              <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                    isClosedRejected(item)
                                      ? "bg-red-100 text-red-700"
                                      : "bg-green-100 text-green-700"
                                  }`}
                                >
                                  {isClosedRejected(item)
                                    ? "Rejected"
                                    : "Approved"}
                                </span>

                                <button
                                  type="button"
                                  title="View History"
                                  onClick={() => setHistoryChecklist(item)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 cursor-pointer"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>

                                <button
                                  type="button"
                                  title="Download Report"
                                  onClick={() =>
                                    handleDownloadReport({
                                      id: item.id,
                                      date: item.created_at
                                        ? new Date(
                                            item.created_at,
                                          ).toLocaleDateString("en-GB", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                          })
                                        : "",
                                    })
                                  }
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-green-200 bg-green-50 text-green-700 transition-colors hover:bg-green-100 cursor-pointer"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Safety Observation Pagination */}
                  {isObservations && (observationPagination.total_pages > 1 || observationPagination.count > 0) && (
                    <div className="mb-6 flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs text-muted-foreground sm:text-sm">
                        Showing Pending for Rectification page {observationPagination.current_page} of {observationPagination.total_pages}
                        {observationPagination.count > 0
                          ? ` • ${observationPagination.count} total observations`
                          : ""}
                      </p>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setRectificationPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={
                            loading ||
                            observationPagination.current_page <= 1
                          }
                          className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Previous
                        </button>

                        <span className="inline-flex min-w-8 items-center justify-center rounded-lg bg-orange-50 px-2.5 py-1.5 text-xs font-semibold text-orange-600">
                          {observationPagination.current_page}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            setRectificationPage((prev) =>
                              Math.min(observationPagination.total_pages, prev + 1),
                            )
                          }
                          disabled={
                            loading ||
                            observationPagination.current_page >=
                              observationPagination.total_pages
                          }
                          className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            )}
          </>
        )}

        {isObservations ? (
          <SafetyObservationReadonlyModal
            open={!!readonlyChecklist}
            checklistId={readonlyChecklist?.id}
            pendingStatusLabel={readonlyPendingStatusLabel}
            onClose={() => setReadonlyChecklist(null)}
          />
        ) : (
          <SafetyChecklistReadonlyModal
            open={!!readonlyChecklist}
            checklistId={readonlyChecklist?.id}
            pendingStatusLabel={readonlyPendingStatusLabel}
            onClose={() => setReadonlyChecklist(null)}
          />
        )}

        <SafetyChecklistHistoryModal
          open={!!historyChecklist}
          checklistId={historyChecklist?.id}
          title={historyChecklist?.name}
          onClose={() => setHistoryChecklist(null)}
        />

        {isSubmitSignatureModalOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
    <div className="w-full max-w-md animate-in zoom-in-95 rounded-2xl bg-white p-6 shadow-2xl">
      <h3 className="mb-4 text-xl font-bold text-slate-800">
        Sign to Submit
      </h3>
      <div className="mb-6 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50">
        <SafetyResponsiveSignatureCanvas ref={sigPadRef} penColor="black" canvasClassName="h-48 rounded-xl" />
      </div>
      <div className="flex justify-end gap-3">
        {/* NEW: standalone Clear button — wipes the pad without closing the modal */}
        <button
          type="button"
          onClick={() => sigPadRef.current?.clear()}
          className="mr-auto rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
        >
          Clear
        </button>

        <button
          type="button"
          onClick={() => {
            sigPadRef.current?.clear();
            setIsSubmitSignatureModalOpen(false);
          }}
          className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            if (sigPadRef.current?.isEmpty()) {
              showToast("Please provide a signature", "error");
              return;
            }
            const sigData = sigPadRef.current
              .getCanvas()
              .toDataURL("image/png");
            fetch(sigData)
              .then((res) => res.blob())
              .then((blob) => {
                const file = new File([blob], "signature.png", {
                  type: "image/png",
                });
                confirmSubmitChecklist(file);
              });
          }}
          className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </div>
  </div>
)}

        {isSignatureModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl rounded-xl bg-white p-5 shadow-xl">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">
                  Final Signature
                </h3>
                <button
                  type="button"
                  onClick={handleCloseSignatureModal}
                  className="rounded p-1 text-muted-foreground hover:bg-muted"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Draw your signature and submit to complete checklist.
              </p>
              <div className="rounded-lg border border-border bg-white p-2">
                <div className="rounded-lg border border-border bg-white p-2">
<SafetyResponsiveSignatureCanvas ref={sigCanvasRef} penColor="black" />
</div>
              </div>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => sigCanvasRef.current?.clear()}
                  className="rounded-lg border px-3 py-1.5 text-xs font-medium text-foreground"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleSubmitWithSignature}
                  disabled={signatureSubmitting}
                  className="rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
                >
                  {signatureSubmitting ? "Submitting..." : "Submit Signature"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
